import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RefreshCw, FileText, X, Eye, EyeOff } from 'lucide-react';
import { apiFetch, getAuthSessionEpoch, useAuthStore } from '../../stores/authStore';
import { describeMarket } from '../../lib/kalshiTicker';
import { gameDetailHrefFromTicker } from '../../lib/kalshiTicker/gameDetailHref';
import { AccountRecordDialog } from '../../components/accountRecord/AccountRecordDialog';
import { recordFromTrade } from '../../components/accountRecord/accountRecordModel';
import { routeHistoricalClick } from '../../components/accountRecord/gameDestination';
import { useAccountRecord, useOpenRecordRow } from '../../components/accountRecord/useAccountRecord';
import { historicalIdentityEpoch, hydrateHistoricalIdentities, useAccountIdentityVersion } from '../../stores/accountMarketIdentity';
import {
  estimateAggregateFeeCents,
  takerRateForTicker,
  makerRateForTicker,
} from '../../lib/pricing/fees';
import { useFeesReady } from '../../services/hydraFees';
import { Toggle } from '../../components/atoms/Toggle';
import { SortableTh } from '../../components/atoms/SortableTh';
import { VenueChip } from '../../components/atoms/VenueChip';
import { usePolymarketRelayRealized } from '../../hooks/usePolymarketRelayRealized';
import { usePolymarketUsMyTrades } from '../../hooks/usePolymarketUsMyTrades';
import { usReadStatus } from '@/lib/polymarketUs/readStatus';
import type { PolyRelayRealizedBucket } from '../../lib/polymarket/relay';
import {
  kalshiVenueEvidence,
  POLY_STALE_THRESHOLD_MS,
  KALSHI_STALE_THRESHOLD_MS,
} from './venueFreshness';
import { VenueFreshnessBadge, RetainedDataBanner } from './freshnessUi';
import {
  selectHeadlinePnl,
  formatSignedDollars,
  type HeadlineVenue,
  type VenuePnlInput,
} from './headlinePnl';
import { summarizeUsPnl, usPnlNotes, usTradeRowsInView } from './usPnl';
import { UsTradeCoverageNotes } from './UsTradeCoverageNotes';
import { MoneyCell, PnlCell, Th, TradePrice, formatDateOnly, tradeMoney, tradePnl, tradeQty } from './tableKit';
import {
  formatDollars,
  formatDollars4,
  formatPriceCents,
  formatTimeOnly,
  positionLabel,
  positionMarketType,
} from './tradeFormat';
import {
  ActionBadge,
  EstMoney,
  Money,
  type ActionLabel,
} from './tradeCells';
import { UnifiedTradesTable, VenueFilterTabs } from './UnifiedTradesTable';
import {
  nextTradeSort,
  sortTrades,
  kalshiRowSortFields,
  tradeVMSortFields,
  type TradeSortKey,
  type TradeSortState,
} from './tradeSort';
import { useWorkingView, useWorkingViewField } from '@/hooks/useWorkingView';
import { measureTopOcclusion, useWorkingScroll } from '@/hooks/useWorkingScroll';
import { WorkingViewReset } from '@/components/atoms/WorkingViewReset';
import {
  tradesReading,
  TRADES_READING_DEFAULTS,
  TRADES_RESET_SCOPE,
  validTradesDateRange,
  type Tab,
  type TimeRange,
  type ViewMode,
} from './readingView';
import { polyTradeLabel } from '../../lib/polymarket/polyTradeLabel';
import { useDisplaySettings } from '../../stores/displaySettings';
import { maskPnl, maskPnlColor } from '../../lib/pnlMask';
import { useMakerRebateActive } from '../../hooks/useMakerRebate';
import { formatMoney } from '../../lib/infra/formatters';
import { useTickerFeedNeeds } from '@/hooks/useTickerFeedNeeds';
import { useMarketStore } from '../../stores/marketStore';
import { CardGrid, InfoTooltip, MarketTypeChip, SideBadge, ViewModeToggle } from '../../components/cards/cardKit';
import { TradesHelp } from '../../components/cards/helpContent';
import { fmtCents, titleParts } from '../../components/cards/cardFormat';
import { TradeCard, type TradeCardVM } from '../../components/cards/TradeCard';
import { polyTradeEconomics } from '../../lib/pricing/polyEconomics';
import { roundToCentiCents } from '../../lib/pricing/subCent';

// ── Types ─────────────────────────────────────────────────────────

/** Preset pills only — 'custom' activates via the date inputs, not a pill. */
const TIME_RANGE_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 Days' },
  { value: '28d', label: '28 Days' },
  { value: 'total', label: 'Total' },
];
const TRADE_VIEW_MODES = ['card', 'table'] as const;

// Mirror of the relay's RealizedBucket (relay/src/services/positions.ts).
export interface RealizedBucket {
  ticker: string;
  side: 'yes' | 'no';
  netCount: number;
  totalBoughtCount: number;
  totalBuyCostCents: number;
  totalSoldCount: number;
  totalSellRevCents: number;
  heldCostBasisCents: number;
  runningRealizedPnlGrossCents: number;
  buyFeesActualCents: number;
  sellFeesActualCents: number;
  hasAnyActualBuyFee: boolean;
  hasAnyActualSellFee: boolean;
  buyFeeKnownFillCount?: number;
  sellFeeKnownFillCount?: number;
  takerBoughtCount: number;
  takerBuyCostCents: number;
  takerSoldCount: number;
  takerSellRevCents: number;
  /** Distinct-fill counts per action. Optional for backward compatibility
   *  with pre-deploy relay responses; when missing, the fee estimator
   *  falls back to the old aggregate-as-one-fill model (under-predicts
   *  multi-fill orders, but no worse than today's behaviour). */
  buyFillCount?: number;
  sellFillCount?: number;
  takerBuyFillCount?: number;
  takerSellFillCount?: number;
  isStillOpen: boolean;
  settlement: 'yes' | 'no' | 'void' | null;
  /** Kalshi scalar settlement's YES-contract redemption value. Null/absent
   *  preserves legacy yes/no and true-refund behavior. */
  settlementValueCents?: number | null;
  lastFillAt: string;
}

interface RawTrade {
  id: string;
  action: string;
  ticker: string | null;
  side: 'yes' | 'no' | null;
  count: number | null;
  priceDollars: string | null;
  kalshiOrderId: string | null;
  status: string;
  credentialScope: string;
  errorMessage: string | null;
  createdAt: string;
}

type RealizedStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface DisplayRow {
  key: string;
  ticker: string;
  side: 'yes' | 'no';
  action: ActionLabel;
  qty: number;
  /** Gross cost basis for this row's qty: avgBuyPrice × qty. Excludes
   *  fees (those are in separate columns for validation). Null for orphan
   *  sells where the buy is outside our visible history. */
  costBasisCents: number | null;
  /** Hydra-schedule estimate for OPENING fees apportioned to this row's qty. */
  openFeeEstCents: number | null;
  /** Kalshi-reported actual OPENING fees apportioned to this row's qty.
   *  Null when fee data is incomplete on the buys. */
  openFeeActualCents: number | null;
  /** Gross close proceeds: sum of sell prices for SELL rows, settlement
   *  credit for SETTLED rows. Excludes close fees. Null for OPEN rows
   *  (position not yet closed). */
  proceedsCents: number | null;
  /** Hydra estimate for CLOSING fees. 0 for SETTLED rows (settlement
   *  doesn't charge fees). */
  closeFeeEstCents: number | null;
  /** Kalshi-reported actual CLOSING fees. 0 for SETTLED rows. Null for
   *  SELL rows when sell fee data is incomplete. */
  closeFeeActualCents: number | null;
  /** Total Cost = costBasis + openFeeActual (what user paid in total to
   *  enter the closed portion). Null when any required input is null. */
  totalCostCents: number | null;
  /** Total Close = proceeds − closeFeeActual (what user received in
   *  total net of close fees). Null when any required input is null. */
  totalCloseCents: number | null;
  /** Net P&L = totalClose − totalCost. Canonical realized P&L for this row. */
  netPnlCents: number | null;
  closedAt: number;
  isRejected: boolean;
  rejectedReason?: string | null;
  /** True for a SETTLED row whose settlement was a void (basis refunded, no
   *  directional outcome). Excluded from the W/L / ROI / dollars-risked stats
   *  so a postponed/cancelled market never reads as a loss or inflates risk —
   *  it still renders as a settled refund row. */
  isVoid?: boolean;
}

// ── Derivation ────────────────────────────────────────────────────

function sellCostBasisCents(b: RealizedBucket, hasOrphan: boolean): number | null {
  if (hasOrphan) return null;
  return b.totalSellRevCents - b.runningRealizedPnlGrossCents;
}

function allocatedFeeCents(totalFeeCents: number, partCount: number, totalCount: number): number {
  return totalCount > 0 ? totalFeeCents * (partCount / totalCount) : 0;
}

/** New relays report exact NULL-vs-known coverage per fill. Fall back to the
 *  old hasAny signal only while a cached frontend is paired with an older
 *  relay response during deployment. */
function actualFeeSideComplete(args: {
  totalCount: number;
  fillCount?: number;
  knownFillCount?: number;
  hasAny: boolean;
}): boolean {
  if (args.totalCount <= 0) return true;
  if (args.fillCount != null && args.knownFillCount != null) {
    return args.fillCount > 0 && args.knownFillCount === args.fillCount;
  }
  return args.hasAny;
}

function buyFeesComplete(b: RealizedBucket): boolean {
  return actualFeeSideComplete({
    totalCount: b.totalBoughtCount,
    fillCount: b.buyFillCount,
    knownFillCount: b.buyFeeKnownFillCount,
    hasAny: b.hasAnyActualBuyFee,
  });
}

function sellFeesComplete(b: RealizedBucket): boolean {
  return actualFeeSideComplete({
    totalCount: b.totalSoldCount,
    fillCount: b.sellFillCount,
    knownFillCount: b.sellFeeKnownFillCount,
    hasAny: b.hasAnyActualSellFee,
  });
}

export function deriveClosedRows(buckets: RealizedBucket[]): DisplayRow[] {
  const rows: DisplayRow[] = [];

  for (const b of buckets) {
    const matchedCount = Math.min(b.totalBoughtCount, b.totalSoldCount);
    const orphanSoldCount = Math.max(
      0,
      b.totalSoldCount - b.totalBoughtCount,
    );
    const heldSettledCount =
      b.netCount > 0 && b.settlement != null ? b.netCount : 0;

    // ── Estimated fees (hydra schedule, taker vs maker portions) ──
    // Aggregate per-fill ceil per side — the single-execution quadratic fee
    // rounded up to the cent. No multi-fill model: Kalshi does not bill split
    // orders more, so the bucket aggregate IS the estimate.
    const takerRate = takerRateForTicker(b.ticker) ?? 0;
    const makerRate = makerRateForTicker(b.ticker) ?? 0;

    const totalBuyFeesEstCents = estimateSideFee({
      takerCount: b.takerBoughtCount,
      takerCostCents: b.takerBuyCostCents,
      makerCount: b.totalBoughtCount - b.takerBoughtCount,
      makerCostCents: b.totalBuyCostCents - b.takerBuyCostCents,
      takerRate,
      makerRate,
    });
    const totalSellFeesEstCents = estimateSideFee({
      takerCount: b.takerSoldCount,
      takerCostCents: b.takerSellRevCents,
      makerCount: b.totalSoldCount - b.takerSoldCount,
      makerCostCents: b.totalSellRevCents - b.takerSellRevCents,
      takerRate,
      makerRate,
    });

    const closedAt = new Date(b.lastFillAt).getTime() || 0;

    // ── SELL row (any sells happened) ──
    if (b.totalSoldCount > 0) {
      const sellQty = b.totalSoldCount;
      const buyFractionForSell =
        b.totalBoughtCount > 0 ? matchedCount / b.totalBoughtCount : 0;

      // Cost basis: only the matched portion has known basis. Orphan
      // portion has no basis (null) — but for simplicity, we still use
      // matched basis here and let totalCost be null when there's
      // orphan involvement.
      const hasOrphan = orphanSoldCount > 0;
      const costBasisCents = sellCostBasisCents(b, hasOrphan);

      // No Math.round — totalBuyFeesEstCents is centicent-precise; rounding
      // here would zero out sub-cent values like a 0.39¢ NHL maker fee.
      // Same applies to actuals — Kalshi's per-fill fee_cost can carry
      // sub-cent precision (centicent, $0.0001) and the bucket sum
      // preserves it; rounding to whole cent on apportionment loses it.
      const openFeeEstCents = totalBuyFeesEstCents * buyFractionForSell;
      const openFeeActualCents =
        buyFeesComplete(b) && !hasOrphan
          ? b.buyFeesActualCents * buyFractionForSell
          : matchedCount === 0 && b.totalBoughtCount === 0
            ? null
            : !buyFeesComplete(b)
              ? null
              : null;

      const proceedsCents = b.totalSellRevCents;
      const closeFeeEstCents = totalSellFeesEstCents;
      const closeFeeActualCents = sellFeesComplete(b)
        ? b.sellFeesActualCents
        : null;

      const totalCostCents =
        costBasisCents == null || openFeeActualCents == null
          ? null
          : costBasisCents + openFeeActualCents;
      const totalCloseCents =
        closeFeeActualCents == null
          ? null
          : proceedsCents - closeFeeActualCents;
      const netPnlCents =
        totalCostCents == null || totalCloseCents == null
          ? null
          : totalCloseCents - totalCostCents;

      rows.push({
        key: `${b.ticker}|${b.side}|SELL`,
        ticker: b.ticker,
        side: b.side,
        action: 'SELL',
        qty: sellQty,
        costBasisCents,
        openFeeEstCents,
        openFeeActualCents,
        proceedsCents,
        closeFeeEstCents,
        closeFeeActualCents,
        totalCostCents,
        totalCloseCents,
        netPnlCents,
        closedAt,
        isRejected: false,
      });
    }

    // ── OPEN row (still-held position with no settlement yet) ──
    // Hidden by the Closed-default filter; surfaces in the All view as
    // visibility into ongoing holdings alongside their closed siblings.
    const openCount =
      b.netCount > 0 && b.settlement == null ? b.netCount : 0;
    if (openCount > 0) {
      const buyFractionForOpen =
        b.totalBoughtCount > 0 ? openCount / b.totalBoughtCount : 0;
      const costBasisCents = b.heldCostBasisCents;
      // Centicent precision preserved (see SELL row note).
      const openFeeEstCents = totalBuyFeesEstCents * buyFractionForOpen;
      const openFeeActualCents = buyFeesComplete(b)
        ? b.buyFeesActualCents * buyFractionForOpen
        : null;
      const totalCostCents =
        openFeeActualCents == null
          ? null
          : costBasisCents + openFeeActualCents;
      rows.push({
        key: `${b.ticker}|${b.side}|OPEN`,
        ticker: b.ticker,
        side: b.side,
        action: 'OPEN',
        qty: openCount,
        costBasisCents,
        openFeeEstCents,
        openFeeActualCents,
        // Position not closed yet — no proceeds, no close fees, no
        // realized P&L. The columns render as "—" via the Money helper.
        proceedsCents: null,
        closeFeeEstCents: null,
        closeFeeActualCents: null,
        totalCostCents,
        totalCloseCents: null,
        netPnlCents: null,
        closedAt,
        isRejected: false,
      });
    }

    // ── SETTLED row (held to maturity, settlement applied) ──
    if (heldSettledCount > 0 && b.settlement != null) {
      const buyFractionForSettled =
        b.totalBoughtCount > 0
          ? heldSettledCount / b.totalBoughtCount
          : 0;

      const costBasisCents = b.heldCostBasisCents;
      // Centicent precision preserved (see SELL row note).
      const openFeeEstCents = totalBuyFeesEstCents * buyFractionForSettled;
      const openFeeActualCents = buyFeesComplete(b)
        ? b.buyFeesActualCents * buyFractionForSettled
        : null;

      // Settlement payout: an authoritative scalar value takes precedence;
      // otherwise 100¢ if won, 0 if lost, or basis returned for a true void.
      let proceedsCents: number;
      if (b.settlementValueCents != null) {
        const payoutCents = b.side === 'yes'
          ? b.settlementValueCents
          : 100 - b.settlementValueCents;
        proceedsCents = payoutCents * heldSettledCount;
      } else if (b.settlement === 'void') {
        proceedsCents = costBasisCents; // basis refunded
      } else {
        const won = b.settlement === b.side;
        proceedsCents = won ? 100 * heldSettledCount : 0;
      }

      // Settlement doesn't charge fees on Kalshi — close fees are 0.
      const closeFeeEstCents = 0;
      const closeFeeActualCents = 0;

      const totalCostCents =
        openFeeActualCents == null
          ? null
          : costBasisCents + openFeeActualCents;
      const totalCloseCents = proceedsCents - closeFeeActualCents;
      const netPnlCents =
        totalCostCents == null ? null : totalCloseCents - totalCostCents;

      rows.push({
        key: `${b.ticker}|${b.side}|SETTLED`,
        ticker: b.ticker,
        side: b.side,
        action: 'SETTLED',
        qty: heldSettledCount,
        costBasisCents,
        openFeeEstCents,
        openFeeActualCents,
        proceedsCents,
        closeFeeEstCents,
        closeFeeActualCents,
        totalCostCents,
        totalCloseCents,
        netPnlCents,
        closedAt,
        isRejected: false,
        isVoid: b.settlement === 'void' && b.settlementValueCents == null,
      });
    }
  }
  return rows;
}

// ── Trader view derivation ────────────────────────────────────────

export interface TraderRow {
  key: string;
  ticker: string;
  side: 'yes' | 'no';
  action: ActionLabel;
  closedAt: number;

  /** Buy side */
  buyQty: number;
  avgBuyPriceCents: number; // 0 when no buys observed (orphan sells)
  /** Gross cost basis: avgBuy × matchedQty, no fees. */
  buyCostCents: number | null;
  /** Kalshi-reported actual buy fees apportioned to this row's matched qty. */
  buyFeeActualCents: number | null;
  /** Total entry cost = buyCostCents + buyFeeActualCents. What the user
   *  actually spent to enter this position, fee-adjusted. Null when
   *  either input is null (orphan sells, or pending fee data). Same
   *  null-fallback shape as cashOutCents. */
  totalInCents: number | null;

  /** Close (sell or settlement) side */
  outQty: number; // 0 for OPEN rows
  avgOutPriceCents: number; // sell avg or settlement-per-contract
  /** Gross close revenue: sum of sell prices for SELL, settlement credit
   *  for SETTLED. Excludes fees. */
  soldCents: number | null;
  /** Kalshi-reported actual sell fees. 0 for SETTLED (no fee on settlement),
   *  null for OPEN (no closes yet) or when actual data missing. */
  sellFeeActualCents: number | null;
  /** Cash actually pocketed on close = soldCents − sellFeeActualCents.
   *  For SETTLED this equals soldCents (settlement is fee-free), so the
   *  field reads as 0 on a loss or qty × $1 on a win. For SELL it's the
   *  sale revenue net of the close fee — what hit the account. Null for
   *  OPEN (no close yet) or when sell-fee data hasn't arrived. */
  cashOutCents: number | null;
  /** Net P&L = CashOut − Cost − BuyFee. Each fee counted once. */
  netPnlCents: number | null;

  isRejected: boolean;
  rejectedReason?: string | null;
  /** See DisplayRow.isVoid — true for a void (refunded) settlement row. */
  isVoid?: boolean;
}

export function deriveTraderRows(buckets: RealizedBucket[]): TraderRow[] {
  const rows: TraderRow[] = [];

  for (const b of buckets) {
    const matchedCount = Math.min(b.totalBoughtCount, b.totalSoldCount);
    const orphanSoldCount = Math.max(
      0,
      b.totalSoldCount - b.totalBoughtCount,
    );
    const heldSettledCount =
      b.netCount > 0 && b.settlement != null ? b.netCount : 0;
    const openCount =
      b.netCount > 0 && b.settlement == null ? b.netCount : 0;
    const hasOrphan = orphanSoldCount > 0;

    const avgBuyPriceCents =
      b.totalBoughtCount > 0
        ? b.totalBuyCostCents / b.totalBoughtCount
        : 0;
    const avgSellPriceCents =
      b.totalSoldCount > 0
        ? b.totalSellRevCents / b.totalSoldCount
        : 0;

    const closedAt = new Date(b.lastFillAt).getTime() || 0;

    // ── OPEN row (still-held, no settlement) ──
    if (openCount > 0) {
      const buyCost = b.heldCostBasisCents;
      const rowAvgBuyPriceCents =
        openCount > 0 ? buyCost / openCount : avgBuyPriceCents;
      const buyFee = buyFeesComplete(b)
        ? allocatedFeeCents(b.buyFeesActualCents, openCount, b.totalBoughtCount)
        : null;
      rows.push({
        key: `${b.ticker}|${b.side}|OPEN`,
        ticker: b.ticker,
        side: b.side,
        action: 'OPEN',
        closedAt,
        buyQty: openCount,
        avgBuyPriceCents: rowAvgBuyPriceCents,
        buyCostCents: buyCost,
        buyFeeActualCents: buyFee,
        totalInCents: buyFee == null ? null : buyCost + buyFee,
        outQty: 0,
        avgOutPriceCents: 0,
        soldCents: null,
        sellFeeActualCents: null,
        cashOutCents: null,
        netPnlCents: null,
        isRejected: false,
      });
    }

    // ── SELL row (any sells happened) ──
    if (b.totalSoldCount > 0) {
      const buyCost = sellCostBasisCents(b, hasOrphan);
      const rowAvgBuyPriceCents =
        buyCost != null && matchedCount > 0
          ? buyCost / matchedCount
          : avgBuyPriceCents;
      const buyFee =
        hasOrphan || !buyFeesComplete(b)
          ? null
          : allocatedFeeCents(b.buyFeesActualCents, matchedCount, b.totalBoughtCount);

      const outQty = b.totalSoldCount;
      const sold = b.totalSellRevCents;
      const rowAvgSellPriceCents = outQty > 0 ? sold / outQty : avgSellPriceCents;
      const sellFee = sellFeesComplete(b)
        ? b.sellFeesActualCents
        : null;

      // Cash actually pocketed = sale revenue − close fee. Null when the
      // close fee hasn't come back from Kalshi yet — same null pattern as
      // netPnlCents, since neither is meaningful without the fee. Brief
      // display blip; fees usually arrive within seconds.
      const cashOut = sellFee == null ? null : sold - sellFee;
      // Net P&L = CashOut − Cost − BuyFee. (CashOut already nets the
      // sell fee, so we don't subtract it again.)
      const netPnl =
        buyCost == null || buyFee == null || cashOut == null
          ? null
          : cashOut - buyCost - buyFee;

      rows.push({
        key: `${b.ticker}|${b.side}|SELL`,
        ticker: b.ticker,
        side: b.side,
        action: 'SELL',
        closedAt,
        buyQty: matchedCount,
        avgBuyPriceCents: rowAvgBuyPriceCents,
        buyCostCents: buyCost,
        buyFeeActualCents: buyFee,
        totalInCents:
          buyCost == null || buyFee == null ? null : buyCost + buyFee,
        outQty,
        avgOutPriceCents: rowAvgSellPriceCents,
        soldCents: sold,
        sellFeeActualCents: sellFee,
        cashOutCents: cashOut,
        netPnlCents: netPnl,
        isRejected: false,
      });
    }

    // ── SETTLED row (held to maturity, settlement applied) ──
    if (heldSettledCount > 0 && b.settlement != null) {
      const buyCost = b.heldCostBasisCents;
      const rowAvgBuyPriceCents =
        heldSettledCount > 0 ? buyCost / heldSettledCount : avgBuyPriceCents;
      const buyFee = buyFeesComplete(b)
        ? allocatedFeeCents(b.buyFeesActualCents, heldSettledCount, b.totalBoughtCount)
        : null;

      let avgOut: number;
      let sold: number;
      if (b.settlementValueCents != null) {
        avgOut = b.side === 'yes'
          ? b.settlementValueCents
          : 100 - b.settlementValueCents;
        sold = avgOut * heldSettledCount;
      } else if (b.settlement === 'void') {
        avgOut = rowAvgBuyPriceCents; // basis returned per-contract
        sold = buyCost;
      } else {
        const won = b.settlement === b.side;
        avgOut = won ? 100 : 0;
        sold = won ? 100 * heldSettledCount : 0;
      }
      // Settlement charges no Kalshi fee; sellFee = 0 (real, not unknown).
      // Cash out at settlement = settlement payout (0 on loss, qty × $1
      // on win, basis on void) — no fee deduction needed.
      const sellFee = 0;
      const cashOut = sold;
      const netPnl =
        buyFee == null ? null : cashOut - buyCost - buyFee;

      rows.push({
        key: `${b.ticker}|${b.side}|SETTLED`,
        ticker: b.ticker,
        side: b.side,
        action: 'SETTLED',
        closedAt,
        buyQty: heldSettledCount,
        avgBuyPriceCents: rowAvgBuyPriceCents,
        buyCostCents: buyCost,
        buyFeeActualCents: buyFee,
        totalInCents: buyFee == null ? null : buyCost + buyFee,
        outQty: heldSettledCount,
        avgOutPriceCents: avgOut,
        soldCents: sold,
        sellFeeActualCents: sellFee,
        cashOutCents: cashOut,
        netPnlCents: netPnl,
        isRejected: false,
        isVoid: b.settlement === 'void' && b.settlementValueCents == null,
      });
    }
  }
  return rows;
}

function deriveRejectedTraderRows(trades: RawTrade[]): TraderRow[] {
  const out: TraderRow[] = [];
  for (const t of trades) {
    if (t.status !== 'rejected') continue;
    const ticker = t.ticker ?? '';
    const side: 'yes' | 'no' = t.side === 'no' ? 'no' : 'yes';
    const qty = t.count ?? 0;
    const priceDollars = t.priceDollars ? Number(t.priceDollars) : 0;
    const avgPriceCents =
      Number.isFinite(priceDollars) && priceDollars > 0
        ? roundToCentiCents(priceDollars * 100)
        : 0;
    const wouldBeCost = avgPriceCents * qty;
    out.push({
      key: `rej:${t.id}`,
      ticker,
      side,
      action: 'REJECTED',
      closedAt: new Date(t.createdAt).getTime(),
      buyQty: qty,
      avgBuyPriceCents: avgPriceCents,
      buyCostCents: wouldBeCost,
      buyFeeActualCents: 0,
      totalInCents: wouldBeCost,
      outQty: 0,
      avgOutPriceCents: 0,
      soldCents: null,
      sellFeeActualCents: null,
      cashOutCents: null,
      netPnlCents: null,
      isRejected: true,
      rejectedReason: t.errorMessage,
    });
  }
  return out;
}

function deriveRejectedRows(trades: RawTrade[]): DisplayRow[] {
  const out: DisplayRow[] = [];
  for (const t of trades) {
    if (t.status !== 'rejected') continue;
    const ticker = t.ticker ?? '';
    const side: 'yes' | 'no' = t.side === 'no' ? 'no' : 'yes';
    const qty = t.count ?? 0;
    const priceDollars = t.priceDollars ? Number(t.priceDollars) : 0;
    const wouldBeCostCents =
      Number.isFinite(priceDollars) && priceDollars > 0 && qty > 0
        ? roundToCentiCents(priceDollars * 100) * qty
        : 0;
    out.push({
      key: `rej:${t.id}`,
      ticker,
      side,
      action: 'REJECTED',
      qty,
      costBasisCents: wouldBeCostCents,
      openFeeEstCents: 0,
      openFeeActualCents: 0,
      proceedsCents: 0,
      closeFeeEstCents: 0,
      closeFeeActualCents: 0,
      totalCostCents: wouldBeCostCents,
      totalCloseCents: 0,
      netPnlCents: null,
      closedAt: new Date(t.createdAt).getTime(),
      isRejected: true,
      rejectedReason: t.errorMessage,
    });
  }
  return out;
}

// ── Filters ───────────────────────────────────────────────────────

function rangeStartMs(range: TimeRange, now = Date.now()): number {
  if (range === 'total' || range === 'custom') return 0;
  if (range === 'today') {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }
  const days = range === '7d' ? 7 : 28;
  return now - days * 24 * 60 * 60 * 1000;
}

interface RangeWindow {
  startMs: number;
  endMs: number;
}

/** Parse a yyyy-mm-dd input value as the first instant of that LOCAL day — its
 *  midnight, unless the clocks skip it — or of the day `addDays` later.
 *  `new Date('2026-07-01')` parses as UTC midnight, which shifts the day
 *  boundary for any non-UTC user — build from parts instead. */
function parseLocalDateMs(iso: string, addDays = 0): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const ms = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + addDays).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** Inclusive filter window for a range selection. Presets are start-only
 *  (endMs = Infinity, today's fills always show). Custom bounds both ends:
 *  start-of-day on the start date through end-of-day on the finish date.
 *  A half-filled custom range degrades gracefully (missing start → epoch,
 *  missing finish → open-ended). */
export function rangeWindowMs(
  range: TimeRange,
  customStart: string,
  customEnd: string,
  now = Date.now(),
): RangeWindow {
  if (range !== 'custom') {
    return { startMs: rangeStartMs(range, now), endMs: Infinity };
  }
  const start = parseLocalDateMs(customStart);
  // The instant before the NEXT day begins, not the finish day + a fixed 24 h:
  // a local day is 23 or 25 hours long when the clocks change (#851). Built
  // from the date's parts, not by moving the finish day's start forward — in a
  // zone whose clocks change AT midnight that start is 01:00, and a day later
  // would still be 01:00.
  const dayAfterEnd = parseLocalDateMs(customEnd, 1);
  return {
    startMs: start ?? 0,
    endMs: dayAfterEnd != null ? dayAfterEnd - 1 : Infinity,
  };
}

// ── Formatters ────────────────────────────────────────────────────
// The display formatters live in tradeFormat.ts (#341); only the link-target
// derivation below stays here, since it builds a route rather than a label.

// GameDetail (beta) link target: `lib/kalshiTicker/gameDetailHref.ts` (moved
// there for #1184 so Recent Fills can name the same candidate). Re-exported for
// the existing tests and callers.
export { gameDetailHrefFromTicker } from '../../lib/kalshiTicker/gameDetailHref';

// ── Stat aggregation ──────────────────────────────────────────────

interface Stats {
  netPnlCents: number;
  feesActualCents: number;
  feesEstCents: number;
  closedCount: number;
  pendingFeeCount: number;
  sellCount: number;
  settledCount: number;
  /** Void (refunded) settlement rows — counted separately so they stay out of
   *  settledCount and the Net P&L sum. */
  voidedCount: number;
}

export function computeStats(rows: DisplayRow[]): Stats {
  // Net P&L, closure counts only — the row-derived stat. Fees are
  // computed separately by computeBucketFees because the row-based
  // sum drops fees on rows where one side's data is null (e.g. when
  // a SELL has no fee_cost yet) and undercounts the buy-fee
  // attribution on partial-close positions (the OPEN-row share is
  // hidden by the Closed-default filter).
  let net = 0;
  let closed = 0;
  let sells = 0;
  let settled = 0;
  let voided = 0;
  for (const r of rows) {
    if (r.isRejected) continue;
    closed += 1;
    // A void (refunded) settlement is a real closed position but carries no
    // directional outcome. Tally it separately and skip it: it stays out of
    // the settled count and the Net P&L sum, so a postponed/cancelled market
    // never moves the headline numbers (matches the relay /pnl, which books a
    // void as $0).
    if (r.isVoid) {
      voided += 1;
      continue;
    }
    if (r.action === 'SELL') sells += 1;
    if (r.action === 'SETTLED') settled += 1;
    if (r.netPnlCents != null) net += r.netPnlCents;
  }
  return {
    netPnlCents: net,
    feesActualCents: 0,
    feesEstCents: 0,
    closedCount: closed,
    pendingFeeCount: 0,
    sellCount: sells,
    settledCount: settled,
    voidedCount: voided,
  };
}

interface BucketFeeStats {
  actualCents: number;
  /** Hydra fee estimate (aggregate per-fill ceil), summed across buckets.
   *  Used for per-row apportioning and the StatCard `est` display. */
  estCents: number;
  pendingCount: number;
}

/** Side-fee estimate: the aggregate fee for the taker portion plus the maker
 *  portion, each rounded up to the cent on the whole portion per Kalshi's
 *  fee-rounding spec ({@link estimateAggregateFeeCents}).
 *
 *  No per-fill model and no calibration coefficient. The earlier design
 *  assumed orders that fill in multiple executions are billed more (each fill
 *  floored to 1¢) and scaled estimates by a fitted coefficient to match. That
 *  premise is false — Kalshi charges the quadratic fee on the executed
 *  quantity, so the single-execution formula IS the estimate. */
function estimateSideFee(args: {
  takerCount: number;
  takerCostCents: number;
  makerCount: number;
  makerCostCents: number;
  takerRate: number;
  makerRate: number;
}): number {
  const { takerCount, takerCostCents, makerCount, makerCostCents, takerRate, makerRate } = args;
  let total = 0;
  if (takerCount > 0) total += estimatePortionFee(takerCostCents, takerCount, takerRate);
  if (makerCount > 0) total += estimatePortionFee(makerCostCents, makerCount, makerRate);
  return total;
}

/** Aggregate fee for one taker/maker portion: ceil the quadratic formula up
 *  to the cent on the whole portion, priced at the portion's average. */
function estimatePortionFee(costCents: number, count: number, rate: number): number {
  if (count <= 0 || rate <= 0) return 0;
  return estimateAggregateFeeCents({
    priceCents: costCents / count,
    count,
    feeRate: rate,
  });
}

/** Sum fees directly from the realized buckets — every cent Kalshi charged
 *  on a fill within the time range — alongside the hydra estimate (aggregate
 *  per-fill ceil) for the est-vs-actual comparison. */
function computeBucketFees(
  buckets: RealizedBucket[],
  startMs: number,
  endMs: number,
): BucketFeeStats {
  let actual = 0;
  let est = 0;
  let pending = 0;
  for (const b of buckets) {
    const closedAt = new Date(b.lastFillAt).getTime() || 0;
    if (closedAt < startMs || closedAt > endMs) continue;

    actual += b.buyFeesActualCents + b.sellFeesActualCents;

    const buyMissing = b.totalBoughtCount > 0 && !buyFeesComplete(b);
    const sellMissing = b.totalSoldCount > 0 && !sellFeesComplete(b);
    if (buyMissing || sellMissing) pending += 1;

    const takerRate = takerRateForTicker(b.ticker) ?? 0;
    const makerRate = makerRateForTicker(b.ticker) ?? 0;

    est += estimateSideFee({
      takerCount: b.takerBoughtCount,
      takerCostCents: b.takerBuyCostCents,
      makerCount: b.totalBoughtCount - b.takerBoughtCount,
      makerCostCents: b.totalBuyCostCents - b.takerBuyCostCents,
      takerRate,
      makerRate,
    });
    est += estimateSideFee({
      takerCount: b.takerSoldCount,
      takerCostCents: b.takerSellRevCents,
      makerCount: b.totalSoldCount - b.takerSoldCount,
      makerCostCents: b.totalSellRevCents - b.takerSellRevCents,
      takerRate,
      makerRate,
    });
  }
  return {
    actualCents: actual,
    estCents: est,
    pendingCount: pending,
  };
}

// ── Price-bucket stats ────────────────────────────────────────────
//
// Aggregates trader rows by entry-price tier (10 buckets, 10¢ each, 0-9¢
// through 90-99¢) so the trader can see how their picks performed by
// confidence level. Uses TraderRow's already-derived per-position
// avg-buy-price + wagered + net-P&L — no fill walking. A 10-contract
// position with avgBuy=78¢ counts entirely in the 70-79¢ bucket, not
// spread across buckets the underlying fills landed in. That matches
// the trader's mental model ("the trade I put on at 78¢") even when
// individual fills slipped a few cents.

interface PriceBucket {
  /** 0..9 — Math.floor(avgBuyPriceCents/10) clamped. Bucket 9 also
   *  captures the rare 100¢ buy (Kalshi caps at 99¢ but fee-inflated
   *  effective entry can round up). */
  index: number;
  rowCount: number;
  contracts: number;
  wageredCents: number;
  /** Sum of netPnlCents across rows where it's non-null. Rows with
   *  null P&L (orphan sells, pending fee data) still bump wagered so
   *  the entry exposure stays honest; ROI then reflects only the
   *  resolved portion of the bucket. */
  pnlCents: number;
  /** Count of rows that contributed P&L — denominator for "still
   *  pending" disclosure. */
  resolvedCount: number;
  /** Sum of buyQty across rows that contributed P&L. Lets the card
   *  show "X of Y contracts resolved" instead of mixing row-count and
   *  contract-count units. */
  resolvedContracts: number;
}

function computeBuckets(rows: TraderRow[]): PriceBucket[] {
  const out: PriceBucket[] = [];
  for (let i = 0; i < 10; i++) {
    out.push({
      index: i,
      rowCount: 0,
      contracts: 0,
      wageredCents: 0,
      pnlCents: 0,
      resolvedCount: 0,
      resolvedContracts: 0,
    });
  }
  for (const r of rows) {
    if (r.isRejected) continue;
    if (r.isVoid) continue; // void refund — neutral to per-entry-price performance
    if (r.buyCostCents == null) continue;
    if (r.avgBuyPriceCents <= 0) continue;
    const idx = Math.max(0, Math.min(9, Math.floor(r.avgBuyPriceCents / 10)));
    const b = out[idx];
    b.rowCount += 1;
    b.contracts += r.buyQty;
    b.wageredCents += r.buyCostCents;
    if (r.netPnlCents != null) {
      b.pnlCents += r.netPnlCents;
      b.resolvedCount += 1;
      b.resolvedContracts += r.buyQty;
    }
  }
  return out;
}

function bucketLabel(i: number): string {
  return `${i * 10}-${i * 10 + 9}¢`;
}

// ── Component ─────────────────────────────────────────────────────

// ── Card-view VM mapping ──────────────────────────────────────────
// Both tabs project their row shape onto the shared TradeCardVM so the
// card grid can render either without caring which tab produced it.

function traderRowToVM(row: TraderRow, eventStartLabel: string | null): TradeCardVM {
  const { title, subtitle, team, marketType } = titleParts(row.ticker, row.side);
  const hasFees =
    row.buyFeeActualCents != null || row.sellFeeActualCents != null;
  return {
    key: row.key,
    venue: 'kalshi',
    ticker: row.ticker,
    title,
    subtitle,
    eventStartLabel,
    team,
    marketType,
    side: row.side,
    action: row.action,
    qty: row.buyQty || row.outQty,
    entryCents: row.avgBuyPriceCents || null,
    exitCents: row.outQty > 0 ? row.avgOutPriceCents : null,
    feesDollars: hasFees
      ? ((row.buyFeeActualCents ?? 0) + (row.sellFeeActualCents ?? 0)) / 100
      : null,
    totalInDollars: row.totalInCents != null ? row.totalInCents / 100 : null,
    cashOutDollars: row.cashOutCents != null ? row.cashOutCents / 100 : null,
    netPnlDollars: row.netPnlCents != null ? row.netPnlCents / 100 : null,
    closedAtMs: row.closedAt,
    isRejected: row.isRejected,
    rejectedReason: row.rejectedReason ?? null,
  };
}

function auditRowToVM(row: DisplayRow, eventStartLabel: string | null): TradeCardVM {
  const { title, subtitle, team, marketType } = titleParts(row.ticker, row.side);
  const hasFees =
    row.openFeeActualCents != null || row.closeFeeActualCents != null;
  return {
    key: row.key,
    venue: 'kalshi',
    ticker: row.ticker,
    title,
    subtitle,
    eventStartLabel,
    team,
    marketType,
    side: row.side,
    action: row.action,
    qty: row.qty,
    entryCents:
      row.costBasisCents != null && row.qty > 0
        ? row.costBasisCents / row.qty
        : null,
    exitCents:
      row.proceedsCents != null && row.qty > 0
        ? row.proceedsCents / row.qty
        : null,
    feesDollars: hasFees
      ? ((row.openFeeActualCents ?? 0) + (row.closeFeeActualCents ?? 0)) / 100
      : null,
    totalInDollars: row.totalCostCents != null ? row.totalCostCents / 100 : null,
    cashOutDollars: row.totalCloseCents != null ? row.totalCloseCents / 100 : null,
    netPnlDollars: row.netPnlCents != null ? row.netPnlCents / 100 : null,
    closedAtMs: row.closedAt,
    isRejected: row.isRejected,
    rejectedReason: row.rejectedReason ?? null,
  };
}

export function MyTrades() {
  const accountIdentityVersion = useAccountIdentityVersion((s) => s.version);
  const historicalReadSeq = useRef(0);
  const historicalReadMounted = useRef(false);
  const [buckets, setBuckets] = useState<RealizedBucket[]>([]);
  const [trades, setTrades] = useState<RawTrade[]>([]);
  const [rebateSummary, setRebateSummary] = useState<{
    estRebateCents: number;
    fillCount: number;
    windowDays: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kalshiRealizedStatus, setKalshiRealizedStatus] =
    useState<RealizedStatus>('idle');
  // Per-venue freshness evidence (#125). Poly's hook exposes lastFetchMs on
  // every attempt, so we snapshot it only on 'ready' (a failed refresh must not
  // reset the age). polyBucketsRetained keeps the last-good Poly rows visible
  // even when the hook blanks them on 'unavailable'.
  //
  // Kalshi is fetched as THREE surfaces (realized rows, rejected orders, rebate
  // summary); each records its own last-success time + last-fetch outcome, so
  // the Kalshi venue can read 'retained' when any currently-VISIBLE surface is
  // showing last-good data after a partial failure — not only when /realized
  // fails (reviewer #158). `kalshiRealizedStatus` still drives realized-only UI
  // (P&L readiness, table loading/empty).
  const [kalshiRealizedOkMs, setKalshiRealizedOkMs] = useState<number | null>(null);
  const [kalshiTradesOkMs, setKalshiTradesOkMs] = useState<number | null>(null);
  const [kalshiTradesLastOk, setKalshiTradesLastOk] = useState(false);
  const [kalshiRebateOkMs, setKalshiRebateOkMs] = useState<number | null>(null);
  const [kalshiRebateLastOk, setKalshiRebateLastOk] = useState(false);
  // Stable "has Kalshi ever loaded" flag for fetchAll's []-deps closure — lets
  // it show the hard-error box only on a true first-load failure, not when a
  // refresh fails with retained rows still on screen.
  const kalshiHasLoadedRef = useRef(false);
  const [polyLastSuccessMs, setPolyLastSuccessMs] = useState<number | null>(null);
  const [polyBucketsRetained, setPolyBucketsRetained] = useState<PolyRelayRealizedBucket[]>([]);

  // #827 — the eight report controls resume through the #824 baseline rather
  // than through eight `useState`s that every route departure discarded. A
  // constant instance because /trades takes no query parameters: there is
  // nothing in the URL to key on, and nothing for a `fromUrl` overlay to
  // outrank.
  //
  // What is NOT resumed is the point of the ticket. `trades`, `buckets`,
  // `rebateSummary` and `polyBucketsRetained` stay in their own state above and
  // `fetchAll` re-runs on every mount — resuming the controls is what makes
  // that fresh read land on the report the trader was actually reading.
  const reading = useWorkingView(tradesReading, 'trades', TRADES_READING_DEFAULTS);
  const [savedRange, setSavedRange] = useWorkingViewField(reading, 'range');
  const [view, setView] = useWorkingViewField(reading, 'view');
  const [tab, setTab] = useWorkingViewField(reading, 'tab');
  const [showRejected, setShowRejected] = useWorkingViewField(reading, 'showRejected');
  const [rawSort, setSort] = useWorkingViewField(reading, 'sort');
  // Table-mode venue filter (#127): 'all' renders the unified cross-venue
  // table; the venue values keep the original venue-specific tables (full
  // Kalshi fee evidence / Poly ledger).
  const [venueFilter, setVenueFilter] = useWorkingViewField(reading, 'venueFilter');
  // Custom range inputs (yyyy-mm-dd). Values persist while a preset pill is
  // selected so toggling back to custom restores the last picked window.
  // A valid edit selects custom and updates both bounds atomically; an invalid
  // intermediate edit remains local until it becomes a valid interval.
  const updateReading = reading.update;
  // Only valid intervals enter the resumption store. A reversed/intermediate
  // edit remains visible and editable here, but departure restores the last
  // valid report. Address + saved date state prevent drafts leaking across
  // auth/history changes or a reset performed by another mounted consumer.
  const dateDraftKey = JSON.stringify([
    reading.address, savedRange, reading.value.customStart, reading.value.customEnd,
  ]);
  const [dateDraft, setDateDraft] = useState<{
    key: string; customStart: string; customEnd: string;
  } | null>(null);
  const activeDateDraft = dateDraft?.key === dateDraftKey ? dateDraft : null;
  const { customStart, customEnd } = activeDateDraft ?? reading.value;
  const range = activeDateDraft ? 'custom' : savedRange;
  function editDate(field: 'customStart' | 'customEnd', value: string) {
    const next = { customStart, customEnd, [field]: value };
    if (validTradesDateRange(next.customStart, next.customEnd)) {
      setDateDraft(null);
      updateReading((prev) => ({ ...prev, ...next, range: 'custom' }));
    } else {
      setDateDraft({ key: dateDraftKey, ...next });
    }
  }
  function setRange(next: TimeRange) {
    setDateDraft(null);
    setSavedRange(next);
  }
  // NOT a redundant re-wrap. `useWorkingView` rebuilds its value by PARSING on
  // every render, so this field arrives as a brand-new object each time even
  // when the key and direction have not moved. The `useState` it replaced was
  // referentially stable, and the two row memos below depend on it BY IDENTITY:
  // without this latch every render that touches anything at all — a refresh
  // tick, a freshness badge — re-sorts both lists and rebuilds every row.
  // #828 measured the same trap on the Tracker at 12 sorts per 10 renders. The
  // Dashboard never hit it because all six of its fields are primitives; `sort`
  // is this route's only object-valued one.
  const sort = useMemo<TradeSortState>(
    () => ({ key: rawSort.key, dir: rawSort.dir }),
    [rawSort.key, rawSort.dir],
  );

  const tradeViewMode = useDisplaySettings((s) => s.tradeViewMode);
  const setTradeViewMode = useDisplaySettings((s) => s.setTradeViewMode);
  // The one definition of "the trade list area shows only Polymarket" —
  // drives the rejected-toggle visibility, the render branch, and the
  // rejected-surface freshness evidence (#158) in lockstep.
  const polyOnlyTable = tradeViewMode === 'table' && venueFilter === 'polymarket';
  // #1031: the US tab shows only the Polymarket US section.
  const usOnlyTable = tradeViewMode === 'table' && venueFilter === 'polymarket_us';
  // Shared P&L privacy preference (same store + key as Position Tracker; #124).
  // hidePnl gates every money surface below: dollars and signed ROI mask to
  // `•••`, per-contract ¢ prices stay visible.
  const showPnl = useDisplaySettings((s) => s.showPnl);
  const toggleShowPnl = useDisplaySettings((s) => s.toggleShowPnl);
  const hidePnl = !showPnl;
  const mmRebateActive = useMakerRebateActive();
  const {
    buckets: polyBucketsLive,
    status: polyStatus,
    lastFetchMs: polyLastFetchMs,
    refresh: refreshPolyRealized,
  } = usePolymarketRelayRealized();
  const usTrades = usePolymarketUsMyTrades();
  const refreshUsTrades = usTrades.refresh;
  // Snapshot the last-good Poly ledger + its success time whenever a fetch
  // succeeds, so retained/unavailable states can keep showing it, aged honestly.
  useEffect(() => {
    if (polyStatus === 'ready') {
      setPolyBucketsRetained(polyBucketsLive);
      setPolyLastSuccessMs(polyLastFetchMs ?? Date.now());
    }
  }, [polyStatus, polyBucketsLive, polyLastFetchMs]);
  const getEventStartTimeMsByTicker = useMarketStore((s) => s.getEventStartTimeMsByTicker);
  const marketVersion = useMarketStore((s) => s.version);
  // Consumer feed filters (feed-filters-per-view): this ledger reads live rows
  // only to label its history (describeMarket, event start times) — the
  // traded tickers, by name. Most are settled and match nothing; what is
  // still live rides the edge instead of the connection carrying every
  // sport the history touches. Undeclared until the first fetch lands.
  const tradesFeedIds = useMemo(
    () => (loading ? null : [
      ...trades.map((t) => t.ticker ?? '').filter(Boolean),
      ...polyBucketsRetained.map((b) => b.ticker),
    ]),
    [loading, trades, polyBucketsRetained],
  );
  useTickerFeedNeeds('my-trades', tradesFeedIds, { fold: 'floor' });

  const eventStartLabelForTicker = useCallback(
    (ticker: string): string => {
      void marketVersion;
      const ms = getEventStartTimeMsByTicker(ticker);
      return ms != null ? formatTimeOnly(ms) : '';
    },
    [getEventStartTimeMsByTicker, marketVersion],
  );

  // `silent` skips the global `loading` flag so a per-venue Kalshi retry drives
  // only its own badge spinner, not the Refresh-all button (#125).
  const fetchAll = useCallback(async (opts?: { silent?: boolean }) => {
    const historySeq = ++historicalReadSeq.current;
    const historyEpoch = historicalIdentityEpoch(), authEpoch = getAuthSessionEpoch();
    const historyUser = useAuthStore.getState().user;
    if (!opts?.silent) setLoading(true);
    setError(null);
    setKalshiRealizedStatus((current) =>
      current === 'idle' ? 'loading' : current,
    );
    const safeFetch = async (url: string) => {
      try {
        const res = await apiFetch(url);
        const data = await res.json().catch(() => null);
        return { ok: res.ok, data };
      } catch {
        return { ok: false, data: null };
      }
    };

    const [realizedRes, tradeRes, rebateRes] = await Promise.all([
      safeFetch('/api/trader/realized'),
      safeFetch('/api/trader/trades?limit=200'),
      safeFetch('/api/trader/rebate-summary'),
    ]);

    const nowMs = Date.now();
    // Each surface records its own outcome so the venue can label a partial
    // failure truthfully. Rows are retained on failure (never blanked) — the
    // badge/banner disclose the retention; zeroing here is the bug #125 fixes.
    if (realizedRes.ok && realizedRes.data) {
      const d = realizedRes.data as { buckets?: RealizedBucket[]; historicalIdentities?: unknown };
      const currentAuth = useAuthStore.getState(), currentUser = currentAuth.user;
      if (Array.isArray(d.buckets) && historicalReadMounted.current
          && currentAuth.status === 'authenticated' && currentUser && historyUser
          && historySeq === historicalReadSeq.current && historyEpoch === historicalIdentityEpoch()
          && authEpoch === getAuthSessionEpoch() && currentUser.id === historyUser.id
          && currentUser.orgId === historyUser.orgId && currentUser.role === historyUser.role) {
        // The existing server-derived bucket set, not a user-selected catalog.
        // Bad optional evidence never changes the financial publication below.
        const tickers = d.buckets.map(b => b && typeof b.ticker === 'string' ? b.ticker : '');
        hydrateHistoricalIdentities(currentUser, d.historicalIdentities, tickers, 'realized');
      }
      setBuckets(Array.isArray(d.buckets) ? d.buckets : []);
      setKalshiRealizedStatus('ready');
      setKalshiRealizedOkMs(nowMs);
      kalshiHasLoadedRef.current = true;
    } else {
      setKalshiRealizedStatus('error');
    }
    setKalshiTradesLastOk(tradeRes.ok);
    if (tradeRes.ok && tradeRes.data) {
      const d = tradeRes.data as { trades?: RawTrade[] };
      setTrades(Array.isArray(d.trades) ? d.trades : []);
      setKalshiTradesOkMs(nowMs);
    }
    setKalshiRebateLastOk(rebateRes.ok);
    if (rebateRes.ok && rebateRes.data) {
      const r = rebateRes.data as {
        estRebateCents?: number;
        fillCount?: number;
        windowDays?: number;
      };
      setRebateSummary({
        estRebateCents: r.estRebateCents ?? 0,
        fillCount: r.fillCount ?? 0,
        windowDays: r.windowDays ?? 30,
      });
      setKalshiRebateOkMs(nowMs);
    }

    // Surface a hard error only when nothing has ever loaded — a failed refresh
    // that still has retained rows is labeled by the badge/banner, not this box.
    const anyOk = [realizedRes, tradeRes].some((r) => r.ok);
    if (!anyOk && !kalshiHasLoadedRef.current) {
      setError('Could not load your trade history.');
    } else if (anyOk) {
      setError(null);
    }
    if (!opts?.silent) setLoading(false);
  }, []);

  useEffect(() => {
    historicalReadMounted.current = true;
    fetchAll();
    return () => { historicalReadMounted.current = false; };
  }, [fetchAll]);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchAll(), refreshPolyRealized(), refreshUsTrades()]);
  }, [fetchAll, refreshPolyRealized, refreshUsTrades]);

  // Kalshi venue freshness accounts for every CURRENTLY-VISIBLE surface (#158):
  // realized rows always show; rejected orders only when showRejected; the
  // rebate card only when MM rebate is active + present. A visible surface whose
  // last fetch failed while it holds prior data means retained Kalshi data is on
  // screen → the venue reads retained, aged from the STALEST visible surface.
  // These are raw evidence (status + last-success), NOT a ticking classification —
  // the aging tick lives in the badge/banner presentation (#158), so this parent
  // doesn't rerender the whole page every 5s.
  const rebateVisible = mmRebateActive && rebateSummary != null;
  const { status: kalshiVenueStatus, lastSuccessMs: kalshiVenueLastSuccessMs } = useMemo(
    () =>
      kalshiVenueEvidence(
        {
          realizedStatus: kalshiRealizedStatus,
          realizedOkMs: kalshiRealizedOkMs,
          tradesOkMs: kalshiTradesOkMs,
          tradesLastOk: kalshiTradesLastOk,
          rebateOkMs: kalshiRebateOkMs,
          rebateLastOk: kalshiRebateLastOk,
        },
        {
          // The rejected-orders surface is off-screen when the table is
          // filtered to Polymarket, so it drops out of the venue evidence
          // (visible-surfaces contract, #158).
          rejectedShown: showRejected && !polyOnlyTable && !usOnlyTable,
          rebateShown: rebateVisible,
        },
      ),
    [
      kalshiRealizedStatus,
      kalshiRealizedOkMs,
      kalshiTradesOkMs,
      kalshiTradesLastOk,
      kalshiRebateOkMs,
      kalshiRebateLastOk,
      showRejected,
      rebateVisible,
      polyOnlyTable,
      usOnlyTable,
    ],
  );

  const feesReady = useFeesReady();
  const allClosed = useMemo(
    () => deriveClosedRows(buckets),
    // feesReady kept so the memo invalidates when hydra rates land mid-mount
    // (rate lookups return null → est 0 until the schedule arrives).
    [buckets, feesReady],
  );
  const allRejected = useMemo(() => deriveRejectedRows(trades), [trades]);

  // Trader-view rows: same buckets, different display shape (per-side
  // qty + avg price, single TOTAL OUT that bakes in all fees).
  const allTrader = useMemo(() => deriveTraderRows(buckets), [buckets]);
  const allTraderRejected = useMemo(
    () => deriveRejectedTraderRows(trades),
    [trades],
  );

  const { startMs, endMs } = useMemo(
    () => rangeWindowMs(range, customStart, customEnd),
    [range, customStart, customEnd],
  );

  // Closed-default filter: hide OPEN rows in 'closed' view; show
  // everything (incl. OPEN holdings) in 'all'.
  const closedInRange = useMemo(
    () =>
      allClosed.filter(
        (r) =>
          r.closedAt >= startMs &&
          r.closedAt <= endMs &&
          (view === 'all' || r.action !== 'OPEN'),
      ),
    [allClosed, startMs, endMs, view],
  );
  const rejectedInRange = useMemo(
    () => allRejected.filter((r) => r.closedAt >= startMs && r.closedAt <= endMs),
    [allRejected, startMs, endMs],
  );

  const stats = useMemo(() => computeStats(closedInRange), [closedInRange]);
  const kalshiPnlPendingCount = useMemo(
    () =>
      closedInRange.filter(
        (row) =>
          !row.isRejected &&
          !row.isVoid &&
          row.action !== 'OPEN' &&
          row.netPnlCents == null,
      ).length,
    [closedInRange],
  );
  // feesReady gates takerRateForTicker: before hydra's fee schedule
  // arrives, every rate lookup returns null (?? 0 in computeBucketFees),
  // so the estimate computes as 0. Buckets typically arrive BEFORE the
  // hydra schedule on a cold load, so without this dep the memo would
  // bake in est=0 and never recompute, producing a phantom "actual ≫
  // estimated" gap on the StatCard.
  const bucketFees = useMemo(
    () => computeBucketFees(buckets, startMs, endMs),
    [buckets, startMs, endMs, feesReady],
  );

  const tableRows = useMemo(() => {
    const merged = showRejected
      ? [...closedInRange, ...rejectedInRange]
      : closedInRange;
    return sortTrades(merged, sort, kalshiRowSortFields);
  }, [closedInRange, rejectedInRange, showRejected, sort]);

  // Trader-view filtering mirrors audit: time range, optional Closed
  // filter (hides OPEN rows), optional rejected toggle.
  const traderInRange = useMemo(
    () =>
      allTrader.filter(
        (r) =>
          r.closedAt >= startMs &&
          r.closedAt <= endMs &&
          (view === 'all' || r.action !== 'OPEN'),
      ),
    [allTrader, startMs, endMs, view],
  );
  const traderRejectedInRange = useMemo(
    () =>
      allTraderRejected.filter(
        (r) => r.closedAt >= startMs && r.closedAt <= endMs,
      ),
    [allTraderRejected, startMs, endMs],
  );
  const traderRows = useMemo(() => {
    const merged = showRejected
      ? [...traderInRange, ...traderRejectedInRange]
      : traderInRange;
    return sortTrades(merged, sort, kalshiRowSortFields);
  }, [traderInRange, traderRejectedInRange, showRejected, sort]);

  // The page headline and the Poly table/cards consume one projection. This
  // prevents the headline from inventing a second accounting recipe and keeps
  // custom-range boundaries identical across both surfaces.
  // Fresh → use the hook's live buckets directly (no one-render lag waiting
  // for the retained-cache effect); not fresh → fall back to the last-good
  // cache so retained/unavailable states keep their rows (#125). This is the
  // ONE place that decides which bucket set counts as "the poly data" —
  // every consumer (VM projection, empty-state discriminator) reads it here.
  const polyBucketsEffective =
    polyStatus === 'ready' ? polyBucketsLive : polyBucketsRetained;
  const polyTradeVMs = useMemo(
    () => buildPolyTradeVMsForRange(polyBucketsEffective, startMs, endMs, view),
    [polyBucketsEffective, startMs, endMs, view],
  );
  const polyPnl = useMemo(
    () => computePolyNetPnlSummary(polyTradeVMs),
    [polyTradeVMs],
  );
  // #1031/#1227: each US position as the K/P OPEN / SELL / SETTLED rows, beside
  // the K/P rows in the merged views and alone under the US tab, windowed and
  // view-filtered row by row as the Poly rows are (R10-F3: Closed hides only
  // OPEN rows). The headline sums exactly these rows' verified values; the
  // notes say what is left out (never counted as 0).
  const usTradeVMs = useMemo(
    () => usTradeRowsInView(usTrades.positions, startMs, endMs, view),
    [usTrades.positions, startMs, endMs, view],
  );
  const usSummary = useMemo(() => summarizeUsPnl(usTradeVMs), [usTradeVMs]);
  const usFailed = usTrades.status === 'error' || usTrades.status === 'unavailable';
  // The relay says when its read is partial: past the account bound whole accounts are missing.
  // Past the fill budget only the newest fills come back, but the rows (#1227) are the positions,
  // which fold the full history, so that bound limits nothing on this page and has no note.
  const usAccountsMissing = usTrades.status === 'ready' && usTrades.reasons.includes('account_limit_exceeded');
  // #1467: an institutional account's history is not in this read yet (its fills reach Recent
  // Fills only; there is no institutional position fold). Partial the same way as a missing account.
  const usInstitutionalUnread = usTrades.status === 'ready' && usTrades.reasons.includes('institutional_history_unread');
  const usPartial = usAccountsMissing || usInstitutionalUnread;
  // #1227: a US reply with no rows is "none" only when nothing was held back from it either: a
  // withheld execution, a missing account, or fills with no position built from them yet (#1230:
  // a holding has no row until something cleared) is not an answer of none.
  const usAnswerIncomplete = usTrades.withheld > 0 || usPartial
    || (usTrades.positions.length === 0 && usTrades.fills.length > 0);
  const usReadNote = usFailed
    ? usReadStatus('trades', usTrades.lastSuccessMs != null ? 'stale' : 'unavailable', { reasons: usTrades.reasons })
    : usAccountsMissing
      ? usReadStatus('trades', 'incomplete', { reasons: ['account_limit_exceeded'], detail: 'Some accounts were not read; totals are incomplete.' })
      : usInstitutionalUnread
        ? usReadStatus('trades', 'incomplete', { reasons: ['institutional_history_unread'],
          detail: 'Institutional account trades are not shown here yet (see Recent Fills); totals are incomplete.' })
        : null;
  const usNotes = [
    ...(usReadNote ? [usReadNote.text] : []),
    ...usPnlNotes(usSummary, usTrades.withheld),
  ];
  const usNoteSummary = [
    usReadNote?.text,
    usSummary.pendingPayoutCount > 0 ? `${usSummary.pendingPayoutCount} US position${usSummary.pendingPayoutCount === 1 ? '' : 's'}: payout unverified` : null,
    usSummary.incompleteCount > 0 ? `${usSummary.incompleteCount} US position${usSummary.incompleteCount === 1 ? '' : 's'} with incomplete P&L` : null,
    usTrades.withheld > 0 ? `${usTrades.withheld} US execution${usTrades.withheld === 1 ? '' : 's'} withheld` : null,
  ].filter(Boolean).join(' · ');
  // The K/P headline shape: any pending US row blanks the US amount. A
  // failed US read is an 'error' here, so it reads "unavailable"/"retained",
  // not Poly's "not connected". Idle means no US read in this session.
  const usHeadline: VenuePnlInput | undefined = usTrades.status === 'idle' ? undefined : {
    netPnlCents: usSummary.netPnlCents,
    closedCount: usSummary.closedCount,
    // Missing accounts or unread institutional history make the US total partial: pending, so the
    // amount blanks (K/P fail-closed).
    pendingCount: usSummary.pendingCount + (usPartial ? 1 : 0),
    status: usTrades.status === 'unavailable' ? 'error' : usTrades.status,
    lastSuccessMs: usTrades.lastSuccessMs,
  };
  const usHasRowsInView = usTradeVMs.length > 0;

  // The headline follows the venue tab (#258). Card mode has no tabs and
  // always interleaves both venues, so its headline stays combined — the same
  // table-only rule polyOnlyTable applies, with the tab selection retained.
  const headlineVenue: HeadlineVenue =
    tradeViewMode === 'table' ? venueFilter : 'all';
  const headline = selectHeadlinePnl({
    venue: headlineVenue,
    kalshi: {
      netPnlCents: stats.netPnlCents,
      closedCount: stats.sellCount + stats.settledCount + stats.voidedCount,
      pendingCount: kalshiPnlPendingCount,
      status: kalshiRealizedStatus,
      lastSuccessMs: kalshiRealizedOkMs,
    },
    poly: {
      netPnlCents: polyPnl.netPnlCents,
      closedCount: polyPnl.closedCount,
      pendingCount: polyPnl.pendingCount,
      status: polyStatus,
      lastSuccessMs: polyLastSuccessMs,
    },
    us: usHeadline,
    usWithheld: usTrades.withheld,
    hidePnl,
  });

  // Card-view VMs — built from whichever tab's rows are in view, so the
  // card grid mirrors the table the toggle replaced.
  const tradeCardVMs = useMemo<TradeCardVM[]>(
    () => {
      // titleParts reads the external identity map. Its narrow revision is
      // part of this projection even when the financial rows stay identical.
      void accountIdentityVersion;
      return tab === 'trader'
        ? traderRows.map((row) => traderRowToVM(row, eventStartLabelForTicker(row.ticker) || null))
        : tableRows.map((row) => auditRowToVM(row, eventStartLabelForTicker(row.ticker) || null));
    },
    [tab, traderRows, tableRows, eventStartLabelForTicker, accountIdentityVersion],
  );

  // Cross-venue merge (#127): the card grid and the unified 'All' table
  // interleave every venue under the one user-selected sort. The Kalshi
  // side follows the active tab exactly like tradeCardVMs; re-sorting the
  // merge is idempotent for the Kalshi half and slots the poly and (#1227)
  // US halves in.
  // #1184 — a Kalshi trade (card, All-venues row, or a table label link whose
  // game the live store does not resolve) opens its read-only account record.
  // Resolved by key from the CURRENT tab's Kalshi VMs on each render, so it
  // repaints with late labels and closes when the row leaves the view.
  const tradeRecord = useAccountRecord();
  const openTradeRecord = tradeRecord.open;
  const recordTrade = useOpenRecordRow(tradeRecord, (key) => tradeCardVMs.find((vm) => vm.key === key && vm.venue === 'kalshi') ?? null);
  const openKalshiCard = useCallback((vm: TradeCardVM) => vm.venue === 'kalshi'
    ? (trigger: HTMLElement | null) => openTradeRecord(vm.key, trigger)
    : undefined, [openTradeRecord]);

  const mergedVMs = useMemo(
    () => sortTrades([...tradeCardVMs, ...polyTradeVMs, ...usTradeVMs], sort, tradeVMSortFields),
    [tradeCardVMs, polyTradeVMs, usTradeVMs, sort],
  );
  // The Polymarket-filtered table follows the same user-selected sort as
  // every other trade list on the page.
  const sortedPolyVMs = useMemo(
    () => sortTrades(polyTradeVMs, sort, tradeVMSortFields),
    [polyTradeVMs, sort],
  );
  const sortedUsVMs = useMemo(
    () => sortTrades(usTradeVMs, sort, tradeVMSortFields),
    [usTradeVMs, sort],
  );

  // Trader-tab StatCard inputs. Risked = total cost basis of every row
  // currently in view (closed-only by default, full set in 'all' view —
  // the same filter the table applies). W/L counts only realized rows
  // (netPnlCents != null); breakevens are tracked but excluded from the
  // headline rate so a 5W/0L/3BE day reads as 100%, not 62%.
  //
  // Contract-weighted W/L uses buyQty as the weight, so a 10-contract
  // win counts 10× a 1-contract loss. This is the stat that compares
  // apples-to-apples against the market's implied probability (avg
  // entry price): if you buy at 65¢ and your contracts win 78% of the
  // time, you're getting a +13pp edge. Row-counted W/L can hide that
  // when a trader sizes up on their high-conviction plays.
  const traderStats = useMemo(() => {
    let dollarsRiskedCents = 0;
    let totalContracts = 0;
    let winCount = 0;
    let lossCount = 0;
    let breakevenCount = 0;
    let winContracts = 0;
    let lossContracts = 0;
    let breakevenContracts = 0;
    let decidedEntryCostCents = 0;
    let decidedContractCount = 0;
    for (const r of traderInRange) {
      if (r.isRejected) continue;
      if (r.isVoid) continue; // void refund — neutral to W/L, ROI, dollars-risked
      if (r.buyCostCents != null) {
        dollarsRiskedCents += r.buyCostCents;
        totalContracts += r.buyQty;
      }
      if (r.netPnlCents == null) continue;
      const qty = r.buyQty;
      if (r.netPnlCents > 0) {
        winCount += 1;
        winContracts += qty;
      } else if (r.netPnlCents < 0) {
        lossCount += 1;
        lossContracts += qty;
      } else {
        breakevenCount += 1;
        breakevenContracts += qty;
      }
      if (r.buyCostCents != null) {
        decidedEntryCostCents += r.buyCostCents;
        decidedContractCount += qty;
      }
    }
    const decided = winCount + lossCount;
    const winRate = decided > 0 ? winCount / decided : null;
    const decidedContracts = winContracts + lossContracts;
    const contractWinRate =
      decidedContracts > 0 ? winContracts / decidedContracts : null;
    const avgEntryCents =
      decidedContractCount > 0
        ? decidedEntryCostCents / decidedContractCount
        : null;
    return {
      dollarsRiskedCents,
      totalContracts,
      winCount,
      lossCount,
      breakevenCount,
      winRate,
      winContracts,
      lossContracts,
      breakevenContracts,
      contractWinRate,
      avgEntryCents,
    };
  }, [traderInRange]);

  const priceBuckets = useMemo(
    () => computeBuckets(traderInRange),
    [traderInRange],
  );

  const handleSort = (k: string) => {
    if (k !== 'pnl' && k !== 'date') return;
    setSort((prev) => nextTradeSort(prev, k as TradeSortKey));
  };

  // #827 — the pane's reading position, on #829's shared primitive.
  //
  // The context carries every control that changes what the list contains or
  // how it is ordered, so an in-place filter edit keeps the current scroll and
  // captures its own anchor for the next return, rather than dragging the old
  // one into a list it no longer describes. `tradeViewMode` is in it because
  // card and table are genuinely different layouts — the same trade has a
  // different row box in each — even though the mode itself is a saved setting
  // this slice does not own.
  const readingScrollContext = JSON.stringify([
    range, customStart, customEnd, view, tab, showRejected, venueFilter,
    sort.key, sort.dir, tradeViewMode,
  ]);
  // Not merely `!loading`. The paint on which a fetch settles still has to
  // render the rows, and the primitive must not seek against a list it is
  // about to grow — the consumer's half of the readiness contract in
  // `260909WorkingScrollRestoration.md`. `mergedVMs` backs card mode and the
  // unified table; the other two back the Kalshi venue tables.
  //
  // It is a union, not the count of whichever surface is currently on screen,
  // and that is a deliberate approximation: under the Polymarket filter these
  // three can be non-empty while the visible poly ledger has no rows, so
  // `ready` goes true with nothing to anchor on. That is safe rather than
  // merely tolerable — the primitive treats an unreachable anchor by keeping
  // the closest attainable position and never triggers a filter change or an
  // unrelated jump (`260909WorkingScrollRestoration.md`). Narrowing this to the
  // visible surface would mean branching on tab, venue and view mode to buy
  // nothing the primitive does not already handle.
  const readingRowsReady =
    !loading && (mergedVMs.length > 0 || tableRows.length > 0 || traderRows.length > 0);
  // Stable identity, deliberately — but NOT because an inline arrow would
  // misbehave. `occlusion` is a layout-effect dependency inside the primitive
  // (`useWorkingScroll.ts:100`) and that effect re-fires `attempt()`, so a fresh
  // function each render re-runs it every render. That is effect churn, not a
  // defect: `attempt()` returns immediately unless a restore is still pending,
  // and while one IS pending those extra calls are the same retries the hook
  // already schedules by animation frame. Checked by reinstating the inline
  // arrow — no test in this suite changes behaviour, which is the honest
  // reading. `useCallback` with no deps keeps the measurement positional (it
  // still reads the live DOM on every call) while not asking React to re-run a
  // layout effect on every render for nothing.
  const readingOcclusion = useCallback(
    (root: HTMLElement) =>
      measureTopOcclusion(root, Array.from(root.querySelectorAll<HTMLElement>('thead.sticky'))),
    [],
  );
  const readingScrollRef = useWorkingScroll({
    pane: 'my-trades-main',
    instance: 'trades',
    context: readingScrollContext,
    ready: readingRowsReady,
    // The trade tables' `<thead>` is `sticky top-0`, so it parks over the pane's
    // top reading edge the moment the reader scrolls — a row restored to that
    // edge would sit BEHIND it rather than at it.
    //
    // Measured positionally at capture and restore rather than latched to a
    // constant, because WHICH table is mounted depends on the tab and venue
    // filter (unified, Kalshi audit, Kalshi trader, Poly ledger) and card mode
    // has no `thead` at all. A fixed number would be wrong in several of those
    // states, and #828 measured the cost of latching a band that moves.
    // Querying inside the callback is what keeps it correct across a mode
    // change without making the band an effect dependency.
    occlusion: readingOcclusion,
  });

  return (
    <div ref={readingScrollRef} className="h-full overflow-auto p-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-muted-foreground" />
            <h2 className="text-lg font-semibold">My Trades</h2>
            <span className="text-muted-foreground text-xs">
              · closed contracts
            </span>
            <InfoTooltip title="My Trades">
              <TradesHelp />
            </InfoTooltip>
          </div>
          <div className="flex items-center gap-2">
            {/* #125: per-venue freshness/retry badges (hidden while fresh),
                mirroring Position Tracker's OrdersSyncBadge. Each retries its own
                venue: Kalshi re-runs the realized/trades fetch, Poly its hook. */}
            <VenueFreshnessBadge
              venue="Kalshi"
              status={kalshiVenueStatus}
              lastSuccessMs={kalshiVenueLastSuccessMs}
              staleThresholdMs={KALSHI_STALE_THRESHOLD_MS}
              onRetry={() => fetchAll({ silent: true })}
            />
            <VenueFreshnessBadge
              venue="Polymarket"
              status={polyStatus}
              lastSuccessMs={polyLastSuccessMs}
              staleThresholdMs={POLY_STALE_THRESHOLD_MS}
              onRetry={refreshPolyRealized}
            />
            <button
              onClick={refreshAll}
              disabled={loading || polyStatus === 'loading'}
              title="Refresh"
              className="border-border bg-card hover:bg-muted flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs"
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />{' '}
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">
            {error}
          </div>
        )}

        {/* #125: retained-data banner — shown when a venue is displaying
            last-known rows (a delayed/failed refresh, or Poly not-connected)
            rather than fresh data. Disappears once both venues are fresh. */}
        <RetainedDataBanner
          kalshi={{
            venue: 'Kalshi',
            status: kalshiVenueStatus,
            lastSuccessMs: kalshiVenueLastSuccessMs,
            staleThresholdMs: KALSHI_STALE_THRESHOLD_MS,
          }}
          poly={{
            venue: 'Polymarket',
            status: polyStatus,
            lastSuccessMs: polyLastSuccessMs,
            staleThresholdMs: POLY_STALE_THRESHOLD_MS,
          }}
        />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <CustomRangePicker
              active={range === 'custom'}
              start={customStart}
              end={customEnd}
              onStart={(v) => editDate('customStart', v)}
              onEnd={(v) => editDate('customEnd', v)}
              onClear={() => {
                setDateDraft(null);
                updateReading((prev) => ({
                  ...prev,
                  customStart: '',
                  customEnd: '',
                  range: TRADES_READING_DEFAULTS.range,
                }));
              }}
            />
            <TimeRangeChips value={range} onChange={setRange} />
          </div>
          <div className="flex items-center gap-2">
            <ViewToggle value={view} onChange={setView} />
            {/* A quiet action at the row's right edge, divided from the toggle
                so it reads as an action rather than another state chip. */}
            <span aria-hidden="true" className="bg-border/60 h-4 w-px" />
            <WorkingViewReset
              label="My Trades"
              scope={TRADES_RESET_SCOPE}
              reset={() => { setDateDraft(null); reading.reset(); }}
              undo={() => { setDateDraft(null); reading.undo(); }}
              canUndo={reading.canUndo}
            />
          </div>
        </div>

        {/* Estimated MM Program rebate — shown only when the org is enrolled
            and the trader has the MM Rebate toggle on. Sums the per-fill
            est_rebate_cents the relay captured at fill time (50% pregame /
            100% live). Uncapped — the program's 30-day cap + eligibility
            aren't modeled; the rebate settles separately with Kalshi. */}
        {mmRebateActive && rebateSummary && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-emerald-500/40 bg-emerald-500/5 px-4 py-3">
            <div className="min-w-0">
              <div className="text-muted-foreground text-[11px] font-medium uppercase tracking-wider">
                Estimated MM Rebate · last {rebateSummary.windowDays}d
              </div>
              <div className="text-muted-foreground/70 text-[11px]">
                {rebateSummary.fillCount.toLocaleString()} maker fill
                {rebateSummary.fillCount === 1 ? '' : 's'} · uncapped estimate, settles separately with Kalshi
              </div>
            </div>
            <span className="shrink-0 font-mono text-2xl font-semibold text-emerald-500">
              {maskPnl(hidePnl, formatMoney(rebateSummary.estRebateCents / 100))}
            </span>
          </div>
        )}

        {/* Realized P&L for the selected venue tab (#258) — combined under
            'All' (and in card mode, which has no tabs). If a folded venue is
            unavailable or fee-incomplete, the label narrows to the venue that
            IS known rather than silently presenting a partial total. */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard
            label={headline.label}
            action={
              // P&L visibility toggle — flips the SAME persisted showPnl the
              // Position Tracker eye controls (#124: no second key/preference).
              <button
                onClick={toggleShowPnl}
                className="text-muted-foreground hover:text-foreground -mr-1 -mt-0.5 rounded p-1 transition-colors"
                title={showPnl ? 'Hide P&L dollars' : 'Show P&L dollars'}
                aria-label={showPnl ? 'Hide P&L dollars' : 'Show P&L dollars'}
                aria-pressed={showPnl}
              >
                {showPnl ? <Eye size={13} /> : <EyeOff size={13} />}
              </button>
            }
            value={
              <span
                className={maskPnlColor(
                  hidePnl,
                  headline.netPnlCents == null
                    ? 'text-muted-foreground'
                    : headline.netPnlCents > 0
                    ? 'text-green-400'
                    : headline.netPnlCents < 0
                      ? 'text-red-400'
                      : 'text-foreground',
                )}
              >
                {maskPnl(
                  hidePnl,
                  headline.netPnlCents == null
                    ? '—'
                    : formatSignedDollars(headline.netPnlCents),
                )}
              </span>
            }
            sub={headline.sub}
          />
        </div>

        {/* Venue banner: every diagnostic card in this grid folds Kalshi
            buckets only, under every venue tab. The realized-P&L card
            immediately above is the one that follows the tab (#258). */}
        <div className="text-muted-foreground -mb-1 text-[10px] font-semibold uppercase tracking-wider">
          Kalshi
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {/* Audit gets calibration diagnostics; trader gets risk + hit-rate
              cards that map onto how a sports bettor evaluates a session. */}
          <StatCard
            label="Closures"
            value={`${stats.sellCount + stats.settledCount + stats.voidedCount}`}
            sub={
              tab === 'trader' && traderStats.winRate !== null
                ? `${stats.sellCount} sold · ${stats.settledCount} settled${
                    stats.voidedCount > 0 ? ` · ${stats.voidedCount} voided` : ''
                  } · ${traderStats.winCount}W·${traderStats.lossCount}L ${(traderStats.winRate * 100).toFixed(0)}%${
                    traderStats.breakevenCount > 0
                      ? ` (${traderStats.breakevenCount}BE)`
                      : ''
                  }`
                : `${stats.sellCount} sold · ${stats.settledCount} settled${
                    stats.voidedCount > 0 ? ` · ${stats.voidedCount} voided` : ''
                  }`
            }
          />
          <StatCard
            label="Fees Paid (Actual)"
            value={maskPnl(hidePnl, formatDollars(bucketFees.actualCents))}
            sub={`est ${maskPnl(hidePnl, formatDollars4(bucketFees.estCents))}`}
          />
          {tab === 'trader' ? (
            <>
              <StatCard
                label="Dollars Risked"
                value={maskPnl(hidePnl, formatDollars(traderStats.dollarsRiskedCents))}
                sub={
                  traderStats.totalContracts > 0
                    ? `${traderStats.totalContracts} contracts`
                    : 'cost basis at stake'
                }
              />
              {/* Contract-weighted W/L: a 10-contract win counts 10× a
                  1-contract loss. The right denominator to compare
                  against the market's implied probability (avg buy
                  price). Sub pairs avg entry with ROI so the card is
                  self-contained: how much did each contract cost on
                  average, and how much did the whole basket return. */}
              <StatCard
                label="W/L by Contracts"
                value={
                  traderStats.contractWinRate === null
                    ? '—'
                    : `${(traderStats.contractWinRate * 100).toFixed(0)}%`
                }
                sub={
                  traderStats.contractWinRate === null ||
                  traderStats.avgEntryCents === null
                    ? 'no decided contracts'
                    : (() => {
                        // avg entry is a per-contract ¢ price (market odds) —
                        // stays visible; ROI is P&L-derived + signed, so it
                        // masks with the dollar figures.
                        const entryStr = `avg entry ${fmtCents(traderStats.avgEntryCents)}`;
                        if (traderStats.dollarsRiskedCents <= 0) {
                          return entryStr;
                        }
                        const roi =
                          (stats.netPnlCents /
                            traderStats.dollarsRiskedCents) *
                          100;
                        const sign = roi > 0 ? '+' : '';
                        return `${entryStr} · ROI ${maskPnl(hidePnl, `${sign}${roi.toFixed(1)}%`)}`;
                      })()
                }
              />
            </>
          ) : (
            // Diagnostic — the hydra fee estimate (aggregate per-fill ceil,
            // same number each per-row Est cell uses, summed across the
            // visible range) next to the est-vs-actual delta so any drift
            // from Kalshi's billed actuals is visible at a glance.
            <StatCard
              label="Hydra Fee Est"
              value={maskPnl(hidePnl, formatDollars4(bucketFees.estCents))}
              sub={
                bucketFees.actualCents > 0
                  ? `actual ${maskPnl(hidePnl, formatDollars(bucketFees.actualCents))} · Δ ${maskPnl(hidePnl, formatDollars4(bucketFees.estCents - bucketFees.actualCents))}`
                  : 'awaiting actuals'
              }
            />
          )}
        </div>

        <PriceBucketGrid buckets={priceBuckets} hidePnl={hidePnl} />

        {/* The Audit/Trader tabs stay visible under every filter — they also
            drive the Kalshi stat cards above, which are always on screen.
            The rejected toggle only affects the Kalshi trade tables, so it
            hides under the Polymarket filter (state retained). */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <TabBar value={tab} onChange={setTab} />
            <ViewModeToggle
              value={tradeViewMode}
              modes={TRADE_VIEW_MODES}
              onChange={setTradeViewMode}
            />
            {/* Venue filter is table-only (#127) — card mode always
                interleaves both venues in one grid. */}
            {tradeViewMode === 'table' && (
              <VenueFilterTabs value={venueFilter} onChange={setVenueFilter} />
            )}
          </div>
          {!polyOnlyTable && !usOnlyTable && (
            <Toggle
              checked={showRejected}
              onChange={setShowRejected}
              label="Show rejected orders"
              size="sm"
            />
          )}
        </div>

        {/* Merged surfaces (card grid, unified 'All' table) interleave both
            venues; the venue-filtered tables keep their original bodies.
            Each venue keeps its own loading lifecycle: the Polymarket branch
            gates on polyStatus (#125), and the merged surfaces only spin
            while there is nothing at all to show — a slow Kalshi fetch must
            not blank already-loaded poly rows. */}
        {usOnlyTable ? (
          (usTrades.status === 'idle' || usTrades.status === 'loading') && !usHasRowsInView ? (
            <LoadingCard />
          ) : usHasRowsInView ? (
            // #1227: the Poly tab's table, with US coverage notes under its header.
            <PolymarketTradesSection
              venue="polymarket_us"
              tradeVMs={sortedUsVMs}
              notes={usNotes}
              noteSummary={usNoteSummary}
              noteDetail={usReadNote?.title}
              sort={sort}
              onSort={handleSort}
              hidePnl={hidePnl}
            />
          ) : (
            // Empty is only asserted from a validated reply; a failed read says so.
            <EmptyCard>
              {usFailed
                ? `${usReadNote?.text ?? 'Polymarket US trades unavailable'}.`
                : usTrades.positions.length > 0
                  ? 'No Polymarket US trades in this range.'
                  : usAnswerIncomplete
                    ? 'No Polymarket US trades to show.'
                    : 'No Polymarket US trades yet.'}
              {usNotes.length > 0 && !usFailed && <>{' '}<UsTradeCoverageNotes summary={usNoteSummary} notes={usNotes} detail={usReadNote?.title} /></>}
            </EmptyCard>
          )
        ) : polyOnlyTable ? (
          polyStatus === 'idle' || polyStatus === 'loading' ? (
            <LoadingCard />
          ) : sortedPolyVMs.length > 0 ? (
            <PolymarketTradesSection
              tradeVMs={sortedPolyVMs}
              sort={sort}
              onSort={handleSort}
              hidePnl={hidePnl}
            />
          ) : (
            // Empty is only asserted from evidence: connection and fetch
            // problems read as unavailability, never as zero trades.
            <EmptyCard>
              {polyStatus === 'unavailable'
                ? 'Polymarket not connected.'
                : polyStatus === 'error' && polyBucketsEffective.length === 0
                  ? 'Polymarket unavailable.'
                  : polyBucketsEffective.length === 0
                    ? 'No Polymarket trades yet.'
                    : 'No Polymarket trades in this range.'}
            </EmptyCard>
          )
        ) : tradeViewMode === 'card' || venueFilter === 'all' ? (
          (loading || polyStatus === 'idle' || polyStatus === 'loading') &&
          mergedVMs.length === 0 ? (
            <LoadingCard />
          ) : mergedVMs.length === 0 ? (
            // #1227: US rows are in the merge, so "no trades" is claimed only
            // once US answered with none too. The US read is never waited on
            // (it has no timeout): while it is out, or after it failed with
            // nothing kept, the K/P answer stands and US is named beside it.
            <EmptyCard>
              {(tab === 'trader'
                ? allTrader.length === 0
                : allClosed.length === 0) && polyBucketsEffective.length === 0
                ? usTrades.positions.length > 0
                  ? 'Nothing in this time range.'
                  : usFailed || usTrades.status === 'loading' || usAnswerIncomplete
                    ? 'No trades to show.'
                    : 'No trades yet.'
                : 'Nothing in this time range.'}
              {usTrades.status === 'loading' ? ' Checking Polymarket US…' : ''}
            </EmptyCard>
          ) : tradeViewMode === 'card' ? (
            <CardGrid>
              {mergedVMs.map((vm) => (
                <TradeCard key={vm.key} vm={vm} hidePnl={hidePnl} onOpen={openKalshiCard(vm)} />
              ))}
            </CardGrid>
          ) : (
            <div className="bg-card border-border overflow-x-auto rounded-lg border">
              <UnifiedTradesTable
                vms={mergedVMs}
                sort={sort}
                onSort={handleSort}
                hidePnl={hidePnl}
                onOpenRecord={openTradeRecord}
              />
            </div>
          )
        ) : loading ? (
          <LoadingCard />
        ) : (tab === 'trader' ? traderRows.length : tableRows.length) === 0 ? (
          <EmptyCard>
            {tab === 'trader'
              ? allTrader.length === 0
                ? 'No trades yet.'
                : 'Nothing in this time range.'
              : allClosed.length === 0
                ? 'No closed contracts yet.'
                : 'Nothing in this time range.'}
          </EmptyCard>
        ) : tab === 'trader' ? (
          <div className="bg-card border-border overflow-x-auto rounded-lg border">
              <table className="w-full text-xs">
                <thead className="bg-muted/30 sticky top-0">
                  <tr className="text-muted-foreground border-border border-b">
                    <SortableTh
                      sortKey="date"
                      activeSortKey={sort.key === 'date' ? 'date' : null}
                      activeSortDir={sort.key === 'date' ? sort.dir : null}
                      onSort={handleSort}
                      className="!px-3 !py-2 align-top !text-[11px]"
                    >
                      Closed
                    </SortableTh>
                    <Th>Market</Th>
                    <Th>Position</Th>
                    <Th align="center">Action</Th>
                    <Th
                      align="right"
                      title="Buy qty with average buy price per contract"
                    >
                      Qty In
                    </Th>
                    <Th
                      align="right"
                      title="Total entry cost: gross cost basis (avg buy × matched qty) plus buy fee — what the user actually spent to enter this position"
                    >
                      Cost
                    </Th>
                    <Th
                      align="right"
                      title="Kalshi-reported actual fee paid to enter this position, apportioned to the closed portion (already baked into Cost — shown separately for transparency)"
                    >
                      Buy Fee
                    </Th>
                    <Th
                      align="right"
                      title="Sold/settled qty with average close price per contract"
                    >
                      Qty Out
                    </Th>
                    <Th
                      align="right"
                      title="Cash actually pocketed on close. SELL: sale revenue minus close fee. SETTLED: 0 (loss) or qty × $1 (win) — settlement is fee-free."
                    >
                      Cash Out
                    </Th>
                    <Th
                      align="right"
                      title="Kalshi-reported actual fee paid to exit. Settlement is fee-free, so blank for SETTLED rows."
                    >
                      Sell Fee
                    </Th>
                    <SortableTh
                      sortKey="pnl"
                      activeSortKey={sort.key === 'pnl' ? 'pnl' : null}
                      activeSortDir={sort.key === 'pnl' ? sort.dir : null}
                      onSort={handleSort}
                      className="!px-3 !py-2 align-top text-right !text-[11px]"
                      title="Cash Out − Cost − Buy Fee (Cash Out already nets the close fee)"
                    >
                      <span className="ml-auto">Net P&L</span>
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {traderRows.map((row) => (
                    <TraderRowComp
                      key={row.key}
                      row={row}
                      eventStartLabel={eventStartLabelForTicker(row.ticker)}
                      hidePnl={hidePnl}
                      onOpenRecord={openTradeRecord}
                    />
                  ))}
                </tbody>
              </table>
          </div>
        ) : (
        <div className="bg-card border-border overflow-x-auto rounded-lg border">
            <table className="w-full text-xs">
              <thead className="bg-muted/30 sticky top-0">
                <tr className="text-muted-foreground border-border border-b">
                  <SortableTh
                    sortKey="date"
                    activeSortKey={sort.key === 'date' ? 'date' : null}
                    activeSortDir={sort.key === 'date' ? sort.dir : null}
                    onSort={handleSort}
                    className="!px-3 !py-2 align-top !text-[11px]"
                  >
                    Closed
                  </SortableTh>
                  <Th>Market</Th>
                  <Th>Position</Th>
                  <Th align="center">Action</Th>
                  <Th align="right">Qty</Th>
                  <Th
                    align="right"
                    title="Cost basis only (avgBuy × qty), no fees"
                  >
                    Buy
                  </Th>
                  <Th
                    align="right"
                    title="Hydra estimate of opening (buy) fees apportioned to this row's qty"
                    sub="estimated"
                  >
                    Open Fee
                  </Th>
                  <Th
                    align="right"
                    title="Kalshi-reported actual opening (buy) fees, apportioned"
                    sub="actual"
                  >
                    Open Fee
                  </Th>
                  <Th
                    align="right"
                    title="Gross close revenue: sum of sell prices for SELL, settlement credit for SETTLED"
                  >
                    Close
                  </Th>
                  <Th
                    align="right"
                    title="Hydra estimate of closing (sell) fees. 0 for SETTLED — Kalshi doesn't charge fees on settlement"
                    sub="estimated"
                  >
                    Close Fee
                  </Th>
                  <Th
                    align="right"
                    title="Kalshi-reported actual closing (sell) fees. 0 for SETTLED"
                    sub="actual"
                  >
                    Close Fee
                  </Th>
                  <Th
                    align="right"
                    title="Total Cost = Cost Basis + Open Fee Actual"
                  >
                    Total In
                  </Th>
                  <Th
                    align="right"
                    title="Total Close = Proceeds − Close Fee Actual"
                  >
                    Total Out
                  </Th>
                  <SortableTh
                    sortKey="pnl"
                    activeSortKey={sort.key === 'pnl' ? 'pnl' : null}
                    activeSortDir={sort.key === 'pnl' ? sort.dir : null}
                    onSort={handleSort}
                    className="!px-3 !py-2 align-top text-right !text-[11px]"
                  >
                    <span className="ml-auto">Net P&L</span>
                  </SortableTh>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row) => (
                  <Row
                    key={row.key}
                    row={row}
                    eventStartLabel={eventStartLabelForTicker(row.ticker)}
                    hidePnl={hidePnl}
                    onOpenRecord={openTradeRecord}
                  />
                ))}
              </tbody>
            </table>
        </div>
        )}

        {/* #1227: US rows share the grid / table; a failed read is named separately
            from a pending amount, even when no retained row lies in this range. */}
        {(tradeViewMode === 'card' || venueFilter === 'all') && (usFailed && !usHasRowsInView ? (
          <p className="text-muted-foreground text-xs" title={usReadNote?.title}>{usReadNote?.text ?? 'Polymarket US trades unavailable'}.</p>
        ) : usNotes.length > 0 ? (
          <UsTradeCoverageNotes summary={usNoteSummary} notes={usNotes} detail={usReadNote?.title} />
        ) : null)}
      </div>
      <AccountRecordDialog
        record={recordTrade ? recordFromTrade(recordTrade, hidePnl, gameDetailHrefFromTicker(recordTrade.ticker)) : null}
        onClose={tradeRecord.close}
      />
    </div>
  );
}

/** Per-venue freshness + retry badge (#125). Hidden while fresh; amber when
 *  retained/unavailable/error, mirroring Position Tracker's OrdersSyncBadge.
 *  Clicking it refreshes that venue.
 *
 *  Classifies + self-ticks from raw evidence here so the aging label doesn't
 *  rerender the whole My Trades page every 5s (#158); useVenueFreshness gates
 *  the tick, so a fresh Kalshi (no age threshold) never ticks. */
function TabBar({
  value,
  onChange,
}: {
  value: Tab;
  onChange: (v: Tab) => void;
}) {
  const opts: { v: Tab; label: string }[] = [
    { v: 'audit', label: 'Audit' },
    { v: 'trader', label: 'Trader' },
  ];
  return (
    <div className="flex items-center gap-1">
      {opts.map((o) => {
        const active = o.v === value;
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
              active
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Subcomponents ─────────────────────────────────────────────────

/** Card-chrome spinner shown while a trade list's data is in flight. */
function LoadingCard() {
  return (
    <div className="bg-card border-border rounded-lg border">
      <div className="flex justify-center py-8">
        <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
      </div>
    </div>
  );
}

/** Card-chrome empty-state message for a trade list with nothing to show. */
function EmptyCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-card border-border rounded-lg border">
      <div className="px-4 py-8 text-center">
        <p className="text-muted-foreground text-sm">{children}</p>
      </div>
    </div>
  );
}

function ViewToggle({
  value,
  onChange,
}: {
  value: ViewMode;
  onChange: (v: ViewMode) => void;
}) {
  const opts: { v: ViewMode; label: string; title: string }[] = [
    { v: 'closed', label: 'Closed', title: 'SELL + SETTLED rows only' },
    { v: 'all', label: 'All', title: 'Include open holdings (no realized P&L yet)' },
  ];
  return (
    <div className="flex items-center gap-1">
      {opts.map((o) => {
        const active = o.v === value;
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            title={o.title}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              active
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Custom start/finish date pair, far left of the range row. Changing either
 *  date activates the 'custom' range (deselecting the preset pills); the
 *  clear button drops back to the 7-day default. Native date inputs — same
 *  house pattern as the G3 panels — so each field opens the browser's
 *  calendar dropdown. */
function CustomRangePicker({
  active,
  start,
  end,
  onStart,
  onEnd,
  onClear,
}: {
  active: boolean;
  start: string;
  end: string;
  onStart: (v: string) => void;
  onEnd: (v: string) => void;
  onClear: () => void;
}) {
  // Width hugs the content (date text + the browser's picker carrot) so the
  // carrot sits directly beside its own date — a wider field pins the carrot
  // to the far right edge, where it reads as belonging to the next input.
  const inputCls =
    'bg-transparent text-foreground w-[6.25rem] cursor-pointer text-xs outline-none';
  return (
    <div
      className={`flex items-center gap-1.5 rounded-full border px-3 py-1 transition-colors ${
        active
          ? 'border-primary/60 bg-primary/10'
          : 'border-border bg-muted hover:bg-muted/80'
      }`}
      title="Custom date range (start – finish)"
    >
      <input
        type="date"
        value={start}
        max={end || undefined}
        onChange={(e) => onStart(e.target.value)}
        aria-label="Custom range start date"
        className={inputCls}
      />
      <span className="text-muted-foreground text-xs">–</span>
      <input
        type="date"
        value={end}
        min={start || undefined}
        onChange={(e) => onEnd(e.target.value)}
        aria-label="Custom range finish date"
        className={inputCls}
      />
      {active && (
        <button
          onClick={onClear}
          title="Clear custom range"
          className="text-muted-foreground hover:text-foreground shrink-0"
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
}

function TimeRangeChips({
  value,
  onChange,
}: {
  value: TimeRange;
  onChange: (v: TimeRange) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      {TIME_RANGE_OPTIONS.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              active
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  action,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  /** Optional top-right control (e.g. the P&L visibility eye toggle). */
  action?: React.ReactNode;
}) {
  return (
    <div className="bg-card border-border rounded-lg border px-4 py-3">
      <div className="flex items-start justify-between gap-2">
        <div className="text-muted-foreground text-xs">{label}</div>
        {action}
      </div>
      <div className="text-foreground mt-1 text-lg font-semibold">{value}</div>
      {sub && (
        <div className="text-muted-foreground mt-0.5 text-[11px]">{sub}</div>
      )}
    </div>
  );
}

// ── Price-bucket grid ─────────────────────────────────────────────
//
// Single row of 10 cards, ascending L→R (0-9¢ longshots through 90-99¢
// near-locks). Collapses to 5×2 on mid-width screens and 2×5 on mobile
// so the cards never get squished narrower than the ROI percentage
// needs. Empty buckets render dimmed (rather than hidden) so the grid
// shape stays stable across time-range filters and the trader can see
// at a glance which confidence tiers they actually play in.

function PriceBucketGrid({
  buckets,
  hidePnl,
}: {
  buckets: PriceBucket[];
  hidePnl: boolean;
}) {
  return (
    <div className="space-y-2">
      <div className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wider">
        Performance by Entry Price
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5 lg:grid-cols-10">
        {buckets.map((b) => (
          <BucketCard
            key={b.index}
            bucket={b}
            tier={b.index >= 5 ? 'favorite' : 'underdog'}
            hidePnl={hidePnl}
          />
        ))}
      </div>
    </div>
  );
}

function BucketCard({
  bucket,
  tier,
  hidePnl,
}: {
  bucket: PriceBucket;
  tier: 'favorite' | 'underdog';
  hidePnl: boolean;
}) {
  const empty = bucket.rowCount === 0;
  const hasResolved = bucket.resolvedCount > 0;
  // ROI uses wagered as the denominator regardless of whether every row
  // resolved — same definition as the page's headline ROI card. When the
  // bucket has any pending-fee rows, we surface the resolved-vs-total
  // counter in the sub-text so the number is interpretable.
  const roi =
    bucket.wageredCents > 0 && hasResolved
      ? bucket.pnlCents / bucket.wageredCents
      : null;
  const roiCls = maskPnlColor(
    hidePnl,
    empty
      ? 'text-muted-foreground'
      : roi == null
        ? 'text-muted-foreground'
        : roi > 0
          ? 'text-green-400'
          : roi < 0
            ? 'text-red-400'
            : 'text-foreground',
  );
  const tierCls =
    tier === 'favorite'
      ? 'bg-green-500/10 text-green-400/80'
      : 'bg-orange-500/10 text-orange-400/80';
  return (
    <div
      className={`bg-card border-border rounded-lg border px-3 py-2 ${empty ? 'opacity-40' : ''}`}
      title={
        empty
          ? `No buys in ${bucketLabel(bucket.index)} range`
          : `${bucket.contracts} contracts across ${bucket.rowCount} trades · avg entry within ${bucketLabel(bucket.index)}`
      }
    >
      <div className="flex items-center justify-between">
        <div className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wider">
          {bucketLabel(bucket.index)}
        </div>
        <span
          className={`rounded px-1 py-0.5 text-[8px] font-bold uppercase tracking-wider ${tierCls}`}
        >
          {tier === 'favorite' ? 'fav' : 'dog'}
        </span>
      </div>
      <div className={`mt-1 text-lg font-semibold ${roiCls}`}>
        {maskPnl(
          hidePnl,
          roi == null ? '—' : `${roi > 0 ? '+' : ''}${(roi * 100).toFixed(1)}%`,
        )}
      </div>
      <div className="text-muted-foreground mt-0.5 text-[10px]">
        {empty
          ? 'no trades'
          : `${maskPnl(hidePnl, formatDollars(bucket.wageredCents))} · ${maskPnl(hidePnl, formatSignedDollars(bucket.pnlCents))}`}
      </div>
      <div className="text-muted-foreground text-[9px] opacity-70">
        {empty
          ? ' '
          : bucket.resolvedContracts < bucket.contracts
            ? `${bucket.resolvedContracts}/${bucket.contracts} contracts resolved`
            : `${bucket.contracts} contracts`}
      </div>
    </div>
  );
}

function Row({
  row,
  eventStartLabel,
  hidePnl,
  onOpenRecord,
}: {
  row: DisplayRow;
  eventStartLabel: string;
  hidePnl: boolean;
  onOpenRecord: (key: string, trigger: HTMLElement) => void;
}) {
  const { marketLabel } = describeMarket(row.ticker, row.side);
  const muted = row.isRejected ? 'opacity-50' : '';
  const tooltip = row.isRejected
    ? `${row.ticker}${row.rejectedReason ? ` — ${row.rejectedReason}` : ''}`
    : row.ticker;
  const gameHref = gameDetailHrefFromTicker(row.ticker);
  return (
    <tr
      data-view-anchor={row.key}
      className={`border-border/40 border-b last:border-0 ${muted}`}
      title={tooltip}
    >
      <td className="text-muted-foreground whitespace-nowrap px-3 py-1.5">
        <div>{formatDateOnly(row.closedAt)}</div>
        <div className="text-[10px] opacity-70">
          {formatTimeOnly(row.closedAt)}
        </div>
      </td>
      <td className="text-foreground whitespace-nowrap px-3 py-1.5 font-mono">
        <div>
          {gameHref ? (
            <Link
              to={gameHref}
              className="hover:text-cyan-300 hover:underline"
              title="Open game detail (beta)"
              // #1184: when the live store does not resolve this game the page
              // would be empty — open the trade's account record instead.
              onClick={(e) => {
                const trigger = e.currentTarget;
                if (routeHistoricalClick(e, gameHref, row.ticker, () => onOpenRecord(row.key, trigger))) e.preventDefault();
              }}
            >
              {marketLabel}
            </Link>
          ) : (
            marketLabel
          )}
        </div>
        {eventStartLabel && (
          <div className="text-muted-foreground text-[10px] font-normal">
            start {eventStartLabel}
          </div>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5">
        <span className="inline-flex flex-col items-center gap-0.5">
          <span
            className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-bold ${
              row.side === 'yes'
                ? 'bg-green-500/15 text-green-400'
                : 'bg-red-500/15 text-red-400'
            }`}
          >
            {positionLabel(row.ticker, row.side)}
          </span>
          <MarketTypeChip marketType={positionMarketType(row.ticker, row.side)} />
        </span>
      </td>
      <td className="px-3 py-1.5 text-center">
        <ActionBadge action={row.action} />
      </td>
      <td className="text-foreground px-3 py-1.5 text-right font-mono">
        {row.qty}
      </td>
      <Money cents={row.costBasisCents} hide={hidePnl} />
      <EstMoney singleFillCents={row.openFeeEstCents} hide={hidePnl} />
      <Money cents={row.openFeeActualCents} precise hide={hidePnl} />
      <Money cents={row.proceedsCents} hide={hidePnl} />
      <EstMoney singleFillCents={row.closeFeeEstCents} hide={hidePnl} />
      <Money cents={row.closeFeeActualCents} precise hide={hidePnl} />
      <Money cents={row.totalCostCents} bold hide={hidePnl} />
      <Money cents={row.totalCloseCents} bold hide={hidePnl} />
      <td
        className={`px-3 py-1.5 text-right font-mono font-medium ${maskPnlColor(
          hidePnl,
          row.netPnlCents == null
            ? 'text-muted-foreground'
            : row.netPnlCents > 0
              ? 'text-green-400'
              : row.netPnlCents < 0
                ? 'text-red-400'
                : 'text-foreground',
        )}`}
      >
        {maskPnl(
          hidePnl,
          row.netPnlCents == null ? '—' : formatSignedDollars(row.netPnlCents),
        )}
      </td>
    </tr>
  );
}

function TraderRowComp({
  row,
  eventStartLabel,
  hidePnl,
  onOpenRecord,
}: {
  row: TraderRow;
  eventStartLabel: string;
  hidePnl: boolean;
  onOpenRecord: (key: string, trigger: HTMLElement) => void;
}) {
  const { marketLabel } = describeMarket(row.ticker, row.side);
  const muted = row.isRejected ? 'opacity-50' : '';
  const tooltip = row.isRejected
    ? `${row.ticker}${row.rejectedReason ? ` — ${row.rejectedReason}` : ''}`
    : row.ticker;
  const gameHref = gameDetailHrefFromTicker(row.ticker);

  // Qty Out is empty for OPEN (still held) and REJECTED (never filled). For
  // SETTLED/SELL it shows count plus the per-contract close price.
  const showOut = row.outQty > 0;

  return (
    <tr
      data-view-anchor={row.key}
      className={`border-border/40 border-b last:border-0 ${muted}`}
      title={tooltip}
    >
      <td className="text-muted-foreground whitespace-nowrap px-3 py-1.5">
        <div>{formatDateOnly(row.closedAt)}</div>
        <div className="text-[10px] opacity-70">
          {formatTimeOnly(row.closedAt)}
        </div>
      </td>
      <td className="text-foreground whitespace-nowrap px-3 py-1.5 font-mono">
        <div>
          {gameHref ? (
            <Link
              to={gameHref}
              className="hover:text-cyan-300 hover:underline"
              title="Open game detail (beta)"
              // #1184: when the live store does not resolve this game the page
              // would be empty — open the trade's account record instead.
              onClick={(e) => {
                const trigger = e.currentTarget;
                if (routeHistoricalClick(e, gameHref, row.ticker, () => onOpenRecord(row.key, trigger))) e.preventDefault();
              }}
            >
              {marketLabel}
            </Link>
          ) : (
            marketLabel
          )}
        </div>
        {eventStartLabel && (
          <div className="text-muted-foreground text-[10px] font-normal">
            start {eventStartLabel}
          </div>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5">
        <span className="inline-flex flex-col items-center gap-0.5">
          <span
            className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-bold ${
              row.side === 'yes'
                ? 'bg-green-500/15 text-green-400'
                : 'bg-red-500/15 text-red-400'
            }`}
          >
            {positionLabel(row.ticker, row.side)}
          </span>
          <MarketTypeChip marketType={positionMarketType(row.ticker, row.side)} />
        </span>
      </td>
      <td className="px-3 py-1.5 text-center">
        <ActionBadge action={row.action} />
      </td>
      <td className="text-foreground whitespace-nowrap px-3 py-1.5 text-right font-mono">
        {row.buyQty > 0 && row.avgBuyPriceCents > 0 ? (
          <>
            {row.buyQty}{' '}
            <span className="opacity-70">
              @ {formatPriceCents(row.avgBuyPriceCents)}
            </span>
          </>
        ) : (
          row.buyQty
        )}
      </td>
      <Money cents={row.totalInCents} hide={hidePnl} />
      <Money cents={row.buyFeeActualCents} muted precise hide={hidePnl} />
      <td className="text-foreground whitespace-nowrap px-3 py-1.5 text-right font-mono">
        {showOut ? (
          <>
            {row.outQty}{' '}
            <span className="opacity-70">
              @ {formatPriceCents(row.avgOutPriceCents)}
            </span>
          </>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <Money cents={row.cashOutCents} hide={hidePnl} />
      {row.action === 'SETTLED' ? (
        <td
          className="text-muted-foreground whitespace-nowrap px-3 py-1.5 text-right font-mono"
          title="No fee — Kalshi doesn't charge on settlement"
        >
          —
        </td>
      ) : (
        <Money cents={row.sellFeeActualCents} muted precise hide={hidePnl} />
      )}
      <td
        className={`whitespace-nowrap px-3 py-1.5 text-right font-mono font-medium ${maskPnlColor(
          hidePnl,
          row.netPnlCents == null
            ? 'text-muted-foreground'
            : row.netPnlCents > 0
              ? 'text-green-400'
              : row.netPnlCents < 0
                ? 'text-red-400'
                : 'text-foreground',
        )}`}
      >
        {maskPnl(
          hidePnl,
          row.netPnlCents == null ? '—' : formatSignedDollars(row.netPnlCents),
        )}
      </td>
    </tr>
  );
}

// ── Polymarket sub-section ────────────────────────────────────────
//
// Replaces the standalone PolymarketRelayFillsPanel that used to mount
// under PositionTracker. Inherits the page's time-range + closed/all
// toggles via the props passed from MyTrades. Hidden when there's
// nothing to show — keeps the page from sprouting empty cards for
// traders whose org doesn't have a poly credential.
//
// Uses the relay's bounded per-market position-state buckets, not paginated
// raw fills. That keeps cost basis complete and carries terminal settlement
// state for positions held to resolution.

/** Exported for tests. Fees are split by action because TradeCardVM folds
 *  buy fees into totalIn and sell fees out of cashOut. */
export interface PolyTradeGroup {
  ticker: string;
  side: 'yes' | 'no';
  marketLabel: string | null;
  marketSport: string | null;
  netCount: number;
  totalBoughtCount: number;
  totalBuyCostCents: number;
  totalSoldCount: number;
  totalSellRevCents: number;
  heldCostBasisCents: number;
  runningRealizedPnlGrossCents: number;
  buyFeeCents: number;
  sellFeeCents: number;
  hasAnyActualBuyFee: boolean;
  hasAnyActualSellFee: boolean;
  buyFillCount?: number;
  sellFillCount?: number;
  buyFeeKnownFillCount?: number;
  sellFeeKnownFillCount?: number;
  settlement: 'yes' | 'no' | 'void' | null;
  settledAtMs: number | null;
  lastFillMs: number;
}

/** Settlement-aware card/table projection of the bounded Poly ledger.
 *  A partially sold position can produce SELL plus OPEN or SETTLED rows;
 *  each row stays on one accounting scope so its totals reconcile. */
export function buildPolyTradeCardVMs(g: PolyTradeGroup): TradeCardVM[] {
  const matched = Math.min(g.totalBoughtCount, g.totalSoldCount);
  const openCount = g.settlement == null ? Math.max(0, g.netCount) : 0;
  const settledCount = g.settlement != null ? Math.max(0, g.netCount) : 0;
  const hasOrphan = g.totalSoldCount > g.totalBoughtCount;
  const buyFeesKnown = actualFeeSideComplete({
    totalCount: g.totalBoughtCount,
    fillCount: g.buyFillCount,
    knownFillCount: g.buyFeeKnownFillCount,
    hasAny: g.hasAnyActualBuyFee,
  });
  const sellFeesKnown = actualFeeSideComplete({
    totalCount: g.totalSoldCount,
    fillCount: g.sellFillCount,
    knownFillCount: g.sellFeeKnownFillCount,
    hasAny: g.hasAnyActualSellFee,
  });
  const avgSell =
    g.totalSoldCount > 0 ? g.totalSellRevCents / g.totalSoldCount : 0;
  const short =
    g.ticker.length > 16
      ? `${g.ticker.slice(0, 6)}…${g.ticker.slice(-4)}`
      : g.ticker;
  const common = {
    venue: 'polymarket',
    ticker: g.ticker,
    title: polyTradeLabel(g.marketLabel, g.ticker),
    subtitle: [g.marketSport?.toUpperCase(), short]
      .filter((x): x is string => !!x)
      .join(' · '),
    eventStartLabel: null,
    side: g.side,
    team: null,
    isRejected: false,
    rejectedReason: null,
  } as const;

  const vms: TradeCardVM[] = [];
  if (openCount > 0) {
    // Pro-rata buy-fee allocation for the still-open portion (Kalshi's
    // allocatedFeeCents shape; poly fees are usually 0 but the contract
    // is fee-inclusive totals).
    const buyFeeOpen =
      buyFeesKnown && g.totalBoughtCount > 0
        ? (g.buyFeeCents * openCount) / g.totalBoughtCount
        : null;
    const cost = g.heldCostBasisCents;
    const openEconomics = polyTradeEconomics({
      qty: openCount,
      totalInDollars: buyFeeOpen == null ? null : (cost + buyFeeOpen) / 100,
      feesDollars: buyFeeOpen == null ? null : buyFeeOpen / 100,
    });
    vms.push({
      ...common,
      key: `poly-${g.ticker}|${g.side}|OPEN`,
      action: 'OPEN',
      qty: openCount,
      entryCents: openCount > 0 ? cost / openCount : null,
      exitCents: null,
      feesDollars: buyFeeOpen == null ? null : buyFeeOpen / 100,
      totalInDollars: buyFeeOpen == null ? null : (cost + buyFeeOpen) / 100,
      cashOutDollars: null,
      netPnlDollars: null,
      closedAtMs: g.lastFillMs,
      riskDollars: openEconomics.riskDollars,
      toWinDollars: openEconomics.toWinDollars,
      juice: openEconomics.juice,
    });
  }
  if (g.totalSoldCount > 0) {
    const buyFeeMatched =
      hasOrphan || !buyFeesKnown || g.totalBoughtCount === 0
        ? null
        : (g.buyFeeCents * matched) / g.totalBoughtCount;
    const matchedCost = hasOrphan
      ? null
      : g.totalSellRevCents - g.runningRealizedPnlGrossCents;
    const totalIn =
      matchedCost == null || buyFeeMatched == null ? null : matchedCost + buyFeeMatched;
    // Cash actually pocketed = sale revenue − close fees.
    const cashOut = sellFeesKnown
      ? g.totalSellRevCents - g.sellFeeCents
      : null;
    const netPnl = totalIn == null || cashOut == null ? null : cashOut - totalIn;
    // Entry economics of the matched (bought-then-sold) portion: risk and
    // to-win describe what was laid at entry; sell-side fees stay in
    // cashOut, not the trio.
    const sellEconomics = polyTradeEconomics({
      qty: matched,
      totalInDollars: totalIn == null ? null : totalIn / 100,
      feesDollars: buyFeeMatched == null ? null : buyFeeMatched / 100,
    });
    vms.push({
      ...common,
      key: `poly-${g.ticker}|${g.side}|SELL`,
      action: 'SELL',
      qty: matched > 0 ? matched : g.totalSoldCount,
      entryCents:
        matchedCost == null || matched <= 0 ? null : matchedCost / matched,
      exitCents: avgSell,
      feesDollars:
        buyFeeMatched == null || !sellFeesKnown
          ? null
          : (buyFeeMatched + g.sellFeeCents) / 100,
      totalInDollars: totalIn == null ? null : totalIn / 100,
      cashOutDollars: cashOut == null ? null : cashOut / 100,
      netPnlDollars: netPnl == null ? null : netPnl / 100,
      closedAtMs: g.lastFillMs,
      riskDollars: sellEconomics.riskDollars,
      toWinDollars: sellEconomics.toWinDollars,
      juice: sellEconomics.juice,
    });
  }
  if (settledCount > 0 && g.settlement != null) {
    const buyFeeSettled =
      buyFeesKnown && g.totalBoughtCount > 0
        ? (g.buyFeeCents * settledCount) / g.totalBoughtCount
        : null;
    const cost = g.heldCostBasisCents;
    const exitCents =
      g.settlement === 'void' ? 50 : g.settlement === g.side ? 100 : 0;
    const cashOut = exitCents * settledCount;
    const totalIn = buyFeeSettled == null ? null : cost + buyFeeSettled;
    const netPnl = totalIn == null ? null : cashOut - totalIn;
    const settledEconomics = polyTradeEconomics({
      qty: settledCount,
      totalInDollars: totalIn == null ? null : totalIn / 100,
      feesDollars: buyFeeSettled == null ? null : buyFeeSettled / 100,
    });
    vms.push({
      ...common,
      key: `poly-${g.ticker}|${g.side}|SETTLED`,
      action: 'SETTLED',
      qty: settledCount,
      entryCents: settledCount > 0 ? cost / settledCount : null,
      exitCents,
      feesDollars: buyFeeSettled == null ? null : buyFeeSettled / 100,
      totalInDollars: totalIn == null ? null : totalIn / 100,
      cashOutDollars: cashOut / 100,
      netPnlDollars: netPnl == null ? null : netPnl / 100,
      closedAtMs: g.settledAtMs ?? g.lastFillMs,
      riskDollars: settledEconomics.riskDollars,
      toWinDollars: settledEconomics.toWinDollars,
      juice: settledEconomics.juice,
    });
  }
  return vms;
}

/** One bounded projection for every My Trades Polymarket consumer. The end
 *  boundary matters for custom historical ranges; the old section applied
 *  only startMs and leaked later rows into the selected period. */
export function buildPolyTradeVMsForRange(
  buckets: PolyRelayRealizedBucket[],
  startMs: number,
  endMs: number,
  view: ViewMode,
): TradeCardVM[] {
  return buckets
    .flatMap((bucket) =>
      buildPolyTradeCardVMs({
        ticker: bucket.ticker,
        side: bucket.side,
        marketLabel: bucket.market_label,
        marketSport: bucket.market_sport,
        netCount: bucket.netCount,
        totalBoughtCount: bucket.totalBoughtCount,
        totalBuyCostCents: bucket.totalBuyCostCents,
        totalSoldCount: bucket.totalSoldCount,
        totalSellRevCents: bucket.totalSellRevCents,
        heldCostBasisCents: bucket.heldCostBasisCents,
        runningRealizedPnlGrossCents: bucket.runningRealizedPnlGrossCents,
        buyFeeCents: bucket.buyFeesActualCents,
        sellFeeCents: bucket.sellFeesActualCents,
        hasAnyActualBuyFee: bucket.hasAnyActualBuyFee,
        hasAnyActualSellFee: bucket.hasAnyActualSellFee,
        buyFillCount: bucket.buyFillCount,
        sellFillCount: bucket.sellFillCount,
        buyFeeKnownFillCount: bucket.buyFeeKnownFillCount,
        sellFeeKnownFillCount: bucket.sellFeeKnownFillCount,
        settlement: bucket.settlement,
        settledAtMs: bucket.settledAt
          ? new Date(bucket.settledAt).getTime()
          : null,
        lastFillMs: new Date(bucket.lastFillAt).getTime(),
      }),
    )
    .filter((vm) => vm.closedAtMs >= startMs && vm.closedAtMs <= endMs)
    .filter((vm) => view === 'all' || vm.action !== 'OPEN')
    .sort((a, b) => b.closedAtMs - a.closedAtMs);
}

export interface PolyNetPnlSummary {
  /** Null means at least one closed row lacks the fee/basis evidence needed
   *  to state a complete Polymarket realized total. */
  netPnlCents: number | null;
  closedCount: number;
  pendingCount: number;
}

/** Sum the exact same closed, fee-aware rows rendered below the headline.
 *  OPEN rows have no realized P&L and never enter either the numerator or
 *  the closure count. Quantize only the final aggregate to the ledger's
 *  centi-cent grain so fractional-contract precision is retained. */
export function computePolyNetPnlSummary(
  tradeVMs: TradeCardVM[],
): PolyNetPnlSummary {
  let netPnlCents = 0;
  let closedCount = 0;
  let pendingCount = 0;

  for (const vm of tradeVMs) {
    if (vm.action !== 'SELL' && vm.action !== 'SETTLED') continue;
    closedCount += 1;
    if (vm.netPnlDollars == null) {
      pendingCount += 1;
      continue;
    }
    netPnlCents += vm.netPnlDollars * 100;
  }

  return {
    netPnlCents:
      pendingCount > 0 ? null : roundToCentiCents(netPnlCents),
    closedCount,
    pendingCount,
  };
}

/** Polymarket-only ledger table — the table-mode 'Polymarket' venue filter.
 *  Poly's fee model differs from Kalshi (no taker/maker schedule we trust
 *  enough to estimate, no per-fill ceil) so this stays a simpler ledger
 *  view: per (ticker, side) aggregated buys/sells with realized P&L,
 *  ordered by the page's shared display sort. */
function PolymarketTradesSection({
  venue = 'polymarket',
  tradeVMs,
  notes = [],
  noteSummary = '',
  noteDetail,
  sort,
  onSort,
  hidePnl,
}: {
  /** #1227: the US tab renders its rows through the same table. */
  venue?: 'polymarket' | 'polymarket_us';
  tradeVMs: TradeCardVM[];
  /** US read notes, shown under the header (#1031). */
  notes?: string[];
  noteSummary?: string;
  noteDetail?: string;
  sort: TradeSortState;
  onSort: (key: string) => void;
  hidePnl: boolean;
}) {
  if (tradeVMs.length === 0) return null;

  return (
    <div
      className="bg-card border-border overflow-x-auto rounded-lg border"
      data-testid={venue === 'polymarket_us' ? 'us-trades-table' : undefined}
    >
      <div className="border-border border-b px-4 py-2.5">
        <div className="flex items-center gap-2">
          <VenueChip venue={venue} />
          <h3 className="text-foreground text-sm font-medium">
            {venue === 'polymarket_us' ? 'Polymarket US Trades' : 'Polymarket Trades'}
          </h3>
        </div>
        {notes.length > 0 && (
          <UsTradeCoverageNotes summary={noteSummary} notes={notes} detail={noteDetail} />
        )}
      </div>
      <table className="w-full text-xs">
        <thead className="bg-muted/30">
          <tr className="text-muted-foreground border-border border-b">
            <SortableTh
              sortKey="date"
              activeSortKey={sort.key === 'date' ? 'date' : null}
              activeSortDir={sort.key === 'date' ? sort.dir : null}
              onSort={onSort}
              className="!px-3 !py-2 align-top !text-[11px]"
            >
              Activity
            </SortableTh>
            <Th>Status</Th>
            <Th>Market</Th>
            <Th align="center">Side</Th>
            <Th align="right">Qty</Th>
            <Th align="right">Entry</Th>
            <Th align="right">Exit</Th>
            <Th align="right">Fees</Th>
            <Th align="right">Total In</Th>
            <Th align="right">Cash Out</Th>
            <SortableTh
              sortKey="pnl"
              activeSortKey={sort.key === 'pnl' ? 'pnl' : null}
              activeSortDir={sort.key === 'pnl' ? sort.dir : null}
              onSort={onSort}
              className="!px-3 !py-2 align-top text-right !text-[11px]"
            >
              <span className="ml-auto">Net P&amp;L</span>
            </SortableTh>
          </tr>
        </thead>
        <tbody>
          {tradeVMs.map((vm) => (
              <tr
                key={vm.key}
                data-view-anchor={vm.key}
                className="border-border/40 border-b last:border-0"
              >
                <td className="text-muted-foreground whitespace-nowrap px-3 py-1.5">
                  {formatDateOnly(vm.closedAtMs)}
                </td>
                <td className="px-3 py-1.5">
                  <span className="bg-muted text-foreground rounded px-1.5 py-0.5 text-[10px] font-semibold">
                    {vm.action}
                  </span>
                </td>
                <td
                  className="text-foreground px-3 py-1.5 font-mono text-[11px]"
                  title={vm.ticker}
                >
                  {vm.title}
                </td>
                <td className="px-3 py-1.5 text-center">
                  <SideBadge side={vm.side} />
                </td>
                <td className="text-foreground px-3 py-1.5 text-right font-mono">
                  {tradeQty(vm)}
                </td>
                <td className="text-muted-foreground px-3 py-1.5 text-right font-mono">
                  <TradePrice cents={vm.entryCents} provisional={vm.us?.entryProvisional} />
                </td>
                <td className="text-muted-foreground px-3 py-1.5 text-right font-mono">
                  <TradePrice cents={vm.exitCents} provisional={vm.us?.exitProvisional} />
                </td>
                <MoneyCell {...tradeMoney(vm, 'fees')} hidePnl={hidePnl} />
                <MoneyCell {...tradeMoney(vm, 'totalIn')} hidePnl={hidePnl} />
                <MoneyCell {...tradeMoney(vm, 'cashOut')} hidePnl={hidePnl} />
                <PnlCell {...tradePnl(vm)} hidePnl={hidePnl} />
              </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
